import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  OutlinedInput,
  LinearProgress,
  Menu,
  Autocomplete,
  Tooltip,
} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import { useSnackbar } from 'notistack';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../../../utils/axios';
import { Close as CloseIcon, Add as AddIcon, Delete as DeleteIcon, CheckCircle, ArrowDropDown as ArrowDropDownIcon, Lock as LockIcon, LockOpen as LockOpenIcon } from '@mui/icons-material';
import TrinhGiamDocPopup from '../../../new_pages/QuanLyVBDen/components/TrinhGiamDocPopup';
import TrinhPhoGiamDocPopup from '../../../new_pages/QuanLyVBDen/components/TrinhPhoGiamDocPopup';
import ChuyenThamMuuPopup from '../../../new_pages/QuanLyVBDen/components/ChuyenThamMuuPopup';
import ChuyenXuLyPopup from '../../../new_pages/common/ChuyenXuLyPopup';
import { SelectChangeEvent } from '@mui/material/Select';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { vi, enUS, zhCN } from 'date-fns/locale';
import { enUS as pickerEnUS, viVN as pickerViVN, zhCN as pickerZhCN } from '@mui/x-date-pickers/locales';
import { PRIORITY_LEVELS, SECURITY_LEVELS, DocsCatalog, fetchDocsCatalogs } from '../documentTypes';
import { modalReopenManager } from '../../../utils/modalReopenManager';
import { MAX_FILE_SIZE, CHUNK_SIZE } from '../../../utils/fileUploadConstants';
import { getCurrentTnLanguage, type TnLanguageCode } from '../../../tuvan_nhathau/language';

interface CreateIncomingDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onReopen?: () => void; // Callback để mở lại dialog sau khi chuyển tham mưu thành công
  mode?: 'create' | 'edit';
  initialData?: {
    id?: number;
    document_number: string;
    document_code: string;
    title: string;
    document_date: string;
    received_date: string;
    sender: string;
    docs_catalog_id: number;
    docs_catalog_name: string;
    docs_group_id: number;
    docs_group_name: string;
    priority: string;
    security_level: string;
    description: string;
    tags: string[];
    deadline: string;
    status: string;
    files?: Array<{
      id: number;
      file_name: string;
      file_path: string;
      file_type: string;
      file_size: number;
      description: string;
    }>;
  };
}

interface FileData {
  id?: number;
  file_name: string;
  file_type: string;
  file_size: number;
  file_path: string;
  description: string;
  file_type_category: string;
  file?: File;
}

// Constants được import từ fileUploadConstants.ts

// Thêm hàm tạo chunks
const createChunks = (file: File): Blob[] => {
  const chunks: Blob[] = [];
  let start = 0;
  
  while (start < file.size) {
    const end = Math.min(start + CHUNK_SIZE, file.size);
    chunks.push(file.slice(start, end));
    start = end;
  }
  
  return chunks;
};

// Thêm hàm chuyển blob thành base64
const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        const base64Content = reader.result.split(',')[1];
        resolve(base64Content);
      } else {
        reject(new Error('Failed to convert blob to base64'));
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
};

// Thêm hàm upload một chunk
const uploadChunk = async (
  documentId: string | number,
  file: FileData,
  chunk: string,
  chunkIndex: number,
  totalChunks: number,
  documentInfo: any
) => {
  // Chuẩn bị payload giống văn bản đi
  const payload = {
    document_id: documentId,
    data: documentInfo,
    file_info: {
      file_name: file.file_name,
      file_type: file.file_type,
      file_type_category: file.file_type_category,
      description: file.description || '',
      total_chunks: totalChunks,
      chunk_number: chunkIndex,
      file_size: file.file_size,
      chunk_data: chunk
    }
  };

  const response = await axiosInstance.post('/incoming-documents/upload-chunk', payload);
  return response.data;
};

// Thêm hàm upload file với chunks
const uploadFileWithChunks = async (
  documentId: string | number,
  file: FileData,
  documentInfo: any,
  onProgress?: (progress: number) => void
) => {
  if (!file.file) {
    throw new Error('No file object provided');
  }

  try {
    // Chia file thành chunks
    const chunks = createChunks(file.file);
    const totalChunks = chunks.length;

    // Upload từng chunk
    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      const base64Chunk = await blobToBase64(chunk);
      
      const result = await uploadChunk(
        documentId,
        file,
        base64Chunk,
        i,
        totalChunks,
        documentInfo
      );
      
      // Tính và báo cáo progress
      const progress = ((i + 1) / totalChunks) * 100;
      if (onProgress) {
        onProgress(progress);
      }

      // Nếu là chunk cuối cùng, trả về kết quả
      if (i === totalChunks - 1) {
        return result;
      }
    }

  } catch (error) {
    console.error('Upload error:', error);
    throw error;
  }
};

interface DocsGroup {
  id: number;
  name: string;
  prefix: string;
  start_number: number;
  number_now: number;
  catalog_ids: number[];
  is_active: boolean;
  order_number?: number;
}

const formatFileSize = (size?: number | null): string => {
  if (!size || isNaN(size) || size <= 0) return '0 KB';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
};

const CreateIncomingDocumentDialog: React.FC<CreateIncomingDocumentDialogProps> = ({
  open,
  onClose,
  onSuccess,
  onReopen,
  mode = 'create',
  initialData
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const lang: TnLanguageCode = getCurrentTnLanguage();
  const dateLocale = lang === 'zh' ? zhCN : lang === 'en' ? enUS : vi;
  const pickerLocaleText =
    lang === 'zh'
      ? pickerZhCN.components.MuiLocalizationProvider.defaultProps.localeText
      : lang === 'en'
      ? pickerEnUS.components.MuiLocalizationProvider.defaultProps.localeText
      : pickerViVN.components.MuiLocalizationProvider.defaultProps.localeText;
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [docsCatalogs, setDocsCatalogs] = useState<DocsCatalog[]>([]);
  const [docsGroups, setDocsGroups] = useState<DocsGroup[]>([]);
  const [checkedDocumentNumber, setCheckedDocumentNumber] = useState(false);
  const [checkingNumber, setCheckingNumber] = useState(false);
  const [numberValid, setNumberValid] = useState<null | boolean>(null);
  const [numberError, setNumberError] = useState<string>('');
  const [originalDocumentNumber, setOriginalDocumentNumber] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [trinhGiamDocOpen, setTrinhGiamDocOpen] = useState(false);
  const [trinhPhoGiamDocOpen, setTrinhPhoGiamDocOpen] = useState(false);
  const [chuyenThamMuuOpen, setChuyenThamMuuOpen] = useState(false);
  const [chuyenXuLyOpen, setChuyenXuLyOpen] = useState(false);
  const [createdDocumentId, setCreatedDocumentId] = useState<string | null>(null);
  const [documentCreated, setDocumentCreated] = useState(false);
  // Lưu trạng thái modal "Tạo mới văn bản" khi mở modal "Chuyển tham mưu"
  const wasCreateDialogOpenRef = useRef<boolean>(false);
  const [documentDetail, setDocumentDetail] = useState<any>(null);
  // Ref để lưu documentDetailData tạm thời khi tạo văn bản
  const documentDetailDataRef = useRef<any>(null);
  // Ref để lưu createdDocumentId tạm thời
  const createdDocumentIdRef = useRef<string | null>(null);
  // State để theo dõi việc cần mở lại dialog sau khi chuyển tham mưu thành công
  const shouldReopenAfterActionRef = useRef<boolean>(false);
  // Ref để lưu onReopen callback để tránh mất trong closure
  const onReopenRef = useRef<(() => void) | undefined>(undefined);
  const [isDocumentNumberLocked, setIsDocumentNumberLocked] = useState(true); // Mặc định khóa khi edit mode
  const [senderSuggestions, setSenderSuggestions] = useState<string[]>([]);
  const [senderSuggestionsLoading, setSenderSuggestionsLoading] = useState(false);
  const senderSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // State cho dialog cảnh báo trùng số ký hiệu
  const [duplicateCodeDialogOpen, setDuplicateCodeDialogOpen] = useState(false);
  const [duplicateCodeData, setDuplicateCodeData] = useState<any>(null);
  const [duplicateCodeType, setDuplicateCodeType] = useState<10 | 11 | 12 | null>(null); // 10: trùng số ký hiệu, 11: trùng file, 12: trùng số ký hiệu ở sổ khác
  // Lưu payload và documentStatus để sử dụng khi người dùng xác nhận tiếp tục
  const pendingUpdateRef = useRef<{ payload: any; documentStatus: string; isDraft: boolean; action?: string } | null>(null);

  // Danh sách các số văn bản còn trống trong sổ hiện tại (lấy từ API available-numbers)
  const [availableNumbers, setAvailableNumbers] = useState<number[]>([]);
  const [loadingAvailableNumbers, setLoadingAvailableNumbers] = useState(false);

  // Tính toán deadline mặc định dựa trên received_date (ngày đến + 7 ngày)
  const getDefaultDeadline = (receivedDate?: string) => {
    try {
      const date = receivedDate ? new Date(receivedDate) : new Date();
      date.setDate(date.getDate() + 7);
      return date.toISOString().split('T')[0];
    } catch (error) {
      console.error('Error calculating default deadline:', error);
      return '';
    }
  };

  const [formData, setFormData] = useState({
    document_number: '',
    document_code: '',
    document_date: (() => {
      try {
        return new Date().toISOString().split('T')[0];
      } catch (error) {
        console.error('Error initializing document_date:', error);
        return '';
      }
    })(),
    received_date: (() => {
      try {
        return new Date().toISOString().split('T')[0];
      } catch (error) {
        console.error('Error initializing received_date:', error);
        return '';
      }
    })(),
    sender: '',
    docs_catalog_id: 0,
    docs_group_id: 0,
    docs_group_name: '',
    priority: 'Thường',
    security_level: 'Thường',
    description: '',
    status: 'cho_xu_ly',
    tags: [] as string[],
    deadline: (() => {
      try {
        const receivedDate = new Date().toISOString().split('T')[0];
        return getDefaultDeadline(receivedDate);
      } catch (error) {
        return '';
      }
    })()
  });

  /** Sổ văn bản hiển thị trong dropdown: khi đã chọn loại văn bản thì chỉ các sổ có loại đó trong catalog_ids */
  const docsGroupsForSelect = useMemo(() => {
    const active = docsGroups.filter((g) => g.is_active);
    const cid = formData.docs_catalog_id;
    if (!cid || cid === 0) {
      return active;
    }
    return active.filter(
      (g) =>
        Array.isArray(g.catalog_ids) &&
        g.catalog_ids.includes(cid)
    );
  }, [docsGroups, formData.docs_catalog_id]);

  const [selectedFiles, setSelectedFiles] = useState<FileData[]>([]);
  const [existingFiles, setExistingFiles] = useState<Array<{
    id: number;
    file_name: string;
    file_path: string;
    file_type: string;
    file_size: number;
    description: string;
  }>>([]);
  const [filesToDelete, setFilesToDelete] = useState<number[]>([]);

  // Track previous open state để chỉ reset khi dialog mới mở
  const prevOpenRef = useRef(false);
  // Track xem người dùng đã chọn loại văn bản chưa (để tránh reset về default)
  const userSelectedCatalogRef = useRef(false);

  // Đăng ký/hủy đăng ký callback với modalReopenManager
  useEffect(() => {
    const modalId = 'create-incoming-document';
    
    // Kiểm tra xem có submenu=van_ban_dientu trong URL không
    // Chỉ đăng ký onReopen khi KHÔNG có submenu=van_ban_dientu
    // (tức là chỉ mở lại modal khi tạo mới từ menu sidebar hoặc nút thêm mới, không mở lại khi từ submenu=van_ban_dientu)
    const urlParams = new URLSearchParams(location.search);
    const submenu = urlParams.get('submenu');
    const isFromDienTuSubmenu = submenu === 'van_ban_dientu';
    
    if (onReopen && !isFromDienTuSubmenu) {
      // Đăng ký callback khi có onReopen prop và KHÔNG phải từ submenu=van_ban_dientu
      // Giữ callback ngay cả khi modal đóng để có thể mở lại sau khi action thành công
      modalReopenManager.register(modalId, onReopen);
      
      // Cleanup: Chỉ hủy đăng ký khi component unmount hoặc onReopen thay đổi
      return () => {
        modalReopenManager.unregister(modalId);
      };
    } else {
      // Nếu không có onReopen hoặc đang ở submenu=van_ban_dientu, hủy đăng ký nếu có
      if (modalReopenManager.hasCallback(modalId)) {
        modalReopenManager.unregister(modalId);
      }
    }
  }, [onReopen, location.search]); // Phụ thuộc vào onReopen và location.search để kiểm tra submenu
  
  // Cập nhật onReopenRef mỗi khi onReopen prop thay đổi (để tương thích với code cũ)
  useEffect(() => {
    onReopenRef.current = onReopen;
  }, [onReopen]);

  // Cleanup timeout khi component unmount hoặc dialog đóng
  useEffect(() => {
    return () => {
      if (senderSearchTimeoutRef.current) {
        clearTimeout(senderSearchTimeoutRef.current);
      }
    };
  }, []);

  // Hàm gọi API lấy danh sách số trống cho một sổ văn bản
  const fetchAvailableNumbersForGroup = useCallback(async (groupId: number) => {
    try {
      setLoadingAvailableNumbers(true);
      const response = await axiosInstance.get('/incoming-documents/available-numbers', {
        params: {
          docs_group_id: groupId,
          page: 1,
          page_size: 200,
          sort: 'desc',
        }
      });
      if (response.data?.code === 0) {
        const nums = response.data?.data?.available_numbers || [];
        setAvailableNumbers(Array.isArray(nums) ? nums : []);
      } else {
        setAvailableNumbers([]);
      }
    } catch {
      setAvailableNumbers([]);
    } finally {
      setLoadingAvailableNumbers(false);
    }
  }, []);

  // Khi docs_group_id thay đổi (ví dụ auto chọn sổ khi mở màn hình nhập mới),
  // tự động gọi API lấy danh sách số trống nếu đã có sổ hợp lệ.
  useEffect(() => {
    if (formData.docs_group_id && formData.docs_group_id !== 0) {
      fetchAvailableNumbersForGroup(formData.docs_group_id);
    } else {
      setAvailableNumbers([]);
    }
  }, [formData.docs_group_id, fetchAvailableNumbersForGroup]);

  // Reset form data và files khi dialog mới mở và có initialData
  useEffect(() => {
    const isDialogJustOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    
    // Chỉ reset khi dialog mới mở, không reset khi initialData thay đổi reference
    if (isDialogJustOpened && initialData) {
      const docNumber = String(initialData.document_number || '').trim();
      const docCode = String(initialData.document_code || '').trim();
      
      // Reset flag khi dialog mới mở
      userSelectedCatalogRef.current = false;
      
      setFormData(prev => {
        // Nếu deadline null hoặc rỗng và có received_date, tự động tính deadline = received_date + 7
        let deadline = initialData.deadline || '';
        if ((!deadline || deadline.trim() === '') && initialData.received_date) {
          deadline = getDefaultDeadline(initialData.received_date);
        }
        
        return {
          ...prev,
          ...initialData,
          deadline: deadline
        };
      });
      setOriginalDocumentNumber(initialData.document_number || '');
      if (initialData.files) {
        setExistingFiles(initialData.files);
      }
      // Nếu có initialData thì văn bản đã tồn tại
      setDocumentCreated(true);
      setCreatedDocumentId(String(initialData.id));
      // Set documentDetail từ initialData
      setDocumentDetail(initialData);
      // Nếu số văn bản rỗng, tự động bỏ lock để lấy số khi chọn sổ văn bản
      if (!docNumber || docNumber === '') {
        setIsDocumentNumberLocked(false);
      } else {
        // Khóa số văn bản khi ở edit mode và có số văn bản
        setIsDocumentNumberLocked(true);
      }
      // Nếu có sổ văn bản, lấy luôn danh sách số còn trống cho sổ đó
      if (initialData.docs_group_id) {
        fetchAvailableNumbersForGroup(initialData.docs_group_id);
      }
    } else if (isDialogJustOpened && mode === 'create') {
      // Reset flag khi dialog mới mở ở create mode
      userSelectedCatalogRef.current = false;
      // Mở khóa số văn bản khi ở create mode
      setIsDocumentNumberLocked(false);
    }
  }, [open, initialData, mode, fetchAvailableNumbersForGroup]);

  // Hàm lấy số max từ API mới
  const getMaxNumber = useCallback(async (docsGroupId: number): Promise<number> => {
    try {
      const response = await axiosInstance.get(`/docs-groups/${docsGroupId}/max-number`);
      if (response.data.code === 0 && response.data.data) {
        return response.data.data.max_number || 0;
      }
      return 0;
    } catch (error) {
      console.error('Error fetching max number:', error);
      // Nếu API lỗi, fallback về 0
      return 0;
    }
  }, []);

  // Tự động chọn "Công văn" và lấy số văn bản khi tạo mới
  useEffect(() => {
    const autoSetCongVanForCreate = async () => {
      // Chỉ xử lý khi:
      // 1. Dialog đang mở
      // 2. Mode = 'create'
      // 3. Không có initialData (tạo mới hoàn toàn)
      // 4. Đã có danh sách catalogs và groups
      // 5. Chưa có loại văn bản được chọn (docs_catalog_id = 0 hoặc rỗng) HOẶC đã có "Công văn" nhưng chưa có số văn bản
      // 6. Người dùng chưa chọn loại văn bản thủ công
      // 7. Chưa có số văn bản (rỗng hoặc 0)
      const docNumberStr = String(formData.document_number || '').trim();
      const hasNoDocumentNumber = !formData.document_number || docNumberStr === '' || docNumberStr === '0';
      const hasNoCatalog = !formData.docs_catalog_id || formData.docs_catalog_id === 0;
      const hasCongVanButNoNumber = formData.docs_catalog_id && docsCatalogs.find(c => c.id === formData.docs_catalog_id)?.name === 'Công văn' && hasNoDocumentNumber;
      
      if (
        open && 
        mode === 'create' && 
        !initialData && 
        docsCatalogs.length > 0 && 
        docsGroups.length > 0 &&
        (hasNoCatalog || hasCongVanButNoNumber) &&
        !userSelectedCatalogRef.current &&
        hasNoDocumentNumber
      ) {
        
        const congVanCatalog = docsCatalogs.find(catalog => catalog.name === 'Công văn');
        
        if (!congVanCatalog) {
          return;
        }
        
        // Lọc các sổ văn bản có catalog_ids chứa ID của "Công văn"
        const matchingGroups = docsGroups.filter(group => 
          group.is_active && 
          group.catalog_ids && 
          Array.isArray(group.catalog_ids) &&
          group.catalog_ids.includes(congVanCatalog.id)
        );
        
        if (matchingGroups.length === 0) {
          return;
        }
        
        // Sắp xếp theo order_number (tăng dần)
        matchingGroups.sort((a, b) => {
          const orderA = a.order_number ?? Number.MAX_SAFE_INTEGER;
          const orderB = b.order_number ?? Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        });
        
        // Chọn sổ có order_number nhỏ nhất
        const selectedGroup = matchingGroups[0];
        
        
        // Xác định group cần dùng
        // Nếu đã có docs_group_id và group đó có chứa "Công văn", dùng group đó
        // Nếu không, dùng selectedGroup (group đầu tiên có "Công văn")
        let targetGroup = selectedGroup;
        if (formData.docs_group_id && formData.docs_group_id !== 0) {
          const existingGroup = docsGroups.find(g => g.id === formData.docs_group_id);
          if (existingGroup && existingGroup.catalog_ids && Array.isArray(existingGroup.catalog_ids) && existingGroup.catalog_ids.includes(congVanCatalog.id)) {
            targetGroup = existingGroup;
          }
        }
        
        // Lấy số max từ API
        try {
          const maxNumber = await getMaxNumber(targetGroup.id);
          const nextNumber = maxNumber + 1;
          
          
          // Kiểm tra lại flag trước khi set (có thể đã thay đổi trong quá trình async)
          if (userSelectedCatalogRef.current) {
            return;
          }
          
          setFormData(prev => {
            // Kiểm tra lại để đảm bảo chưa có loại văn bản được chọn bởi người dùng
            // Nếu đã có "Công văn" nhưng khác với congVanCatalog.id, skip
            const currentCatalogName = prev.docs_catalog_id ? docsCatalogs.find(c => c.id === prev.docs_catalog_id)?.name : null;
            if (userSelectedCatalogRef.current || (prev.docs_catalog_id && prev.docs_catalog_id !== 0 && prev.docs_catalog_id !== congVanCatalog.id && currentCatalogName !== 'Công văn')) {
              return prev;
            }
            
            // Nếu đã có số văn bản, không ghi đè
            const prevDocNumberStr = String(prev.document_number || '').trim();
            if (prev.document_number && prevDocNumberStr !== '' && prevDocNumberStr !== '0') {
              return prev;
            }
            
            // Nếu đã có "Công văn", chỉ update số văn bản và group (nếu cần)
            // Nếu chưa có, set cả catalog, group và số
            const shouldSetCatalog = !prev.docs_catalog_id || prev.docs_catalog_id === 0;
            
            return {
              ...prev,
              ...(shouldSetCatalog && {
                docs_catalog_id: congVanCatalog.id
              }),
              docs_group_id: targetGroup.id,
              docs_group_name: targetGroup.name,
              document_number: String(nextNumber)
            };
          });
        } catch (error) {
          console.error('[useEffect autoSetCongVanForCreate] Lỗi khi lấy số văn bản:', error);
        }
      }
    };
    
    autoSetCongVanForCreate();
  }, [open, mode, initialData, docsCatalogs, docsGroups, formData.docs_catalog_id, formData.document_number, formData.docs_group_id, getMaxNumber]);

  // Hàm kiểm tra số văn bản
  const checkDocumentNumber = useCallback(async (documentNumber: string, showSnackbar = false) => {
    // Đảm bảo documentNumber là string trước khi gọi .trim()
    const docNumber = String(documentNumber || '').trim();
    if (!docNumber) {
      setNumberValid(null);
      setNumberError('');
      return;
    }

    // Nếu đang edit và số văn bản giống với số ban đầu thì không cần check
    const originalNumber = String(originalDocumentNumber || '').trim();
    if (mode === 'edit' && docNumber === originalNumber) {
      setNumberValid(true);
      setNumberError('');
      return;
    }

    setCheckingNumber(true);
    setNumberError('');
    try {
      const response = await axiosInstance.post('/incoming-documents/check_number_avaiable', {
        document_number: docNumber,
        catalog_id: formData.docs_group_id,
        document_code: formData.document_code,
        id: initialData?.id || undefined
      });
      if (response.data.code === 0) {
        setNumberValid(true);
        if (showSnackbar) {
          enqueueSnackbar('Số văn bản có thể sử dụng!', { variant: 'success' });
        }
      } else {
        setNumberValid(false);
        setNumberError(response.data.msg || 'Số văn bản không hợp lệ!');
      }
    } catch (error: any) {
      setNumberValid(false);
      const msg = error?.response?.data?.msg || 'Lỗi kiểm tra số văn bản!';
      setNumberError(msg);
    } finally {
      setCheckingNumber(false);
    }
  }, [formData.docs_group_id, formData.document_code, mode, originalDocumentNumber, initialData?.id, enqueueSnackbar]);

  // Fetch danh sách loại văn bản
  useEffect(() => {
    if (open) {
      loadDocsCatalogs();
      loadDocsGroups();
    }
  }, [open]);

  // Tự động chọn loại văn bản và sổ văn bản khi edit mode và số văn bản trống
  useEffect(() => {
    const autoSelectCatalogAndGroup = async () => {
      
      // Kiểm tra xem người dùng đã chọn loại văn bản chưa
      if (userSelectedCatalogRef.current) {
        return;
      }
      
      // Chỉ xử lý khi ở edit mode, có initialData, số văn bản trống, và đã load xong catalogs và groups
      if (mode === 'edit' && open && initialData && docsCatalogs.length > 0 && docsGroups.length > 0) {
        const docNumber = String(initialData.document_number || '').trim();
        const currentDocNumber = String(formData.document_number || '').trim();
        const currentCatalogId = formData.docs_catalog_id;
        const isLocked = isDocumentNumberLocked;
        
        
        // Chỉ xử lý nếu số văn bản trống VÀ không bị lock VÀ formData chưa có số văn bản
        if ((!docNumber || docNumber === '') && !isLocked && (!currentDocNumber || currentDocNumber === '')) {
          // Nếu đã có loại văn bản trong initialData, tìm sổ văn bản tương ứng
          if (initialData.docs_catalog_id && initialData.docs_catalog_id > 0) {
            const matchingGroups = docsGroups.filter(group => 
              group.is_active && 
              group.catalog_ids && 
              Array.isArray(group.catalog_ids) &&
              group.catalog_ids.includes(initialData.docs_catalog_id)
            );

            if (matchingGroups.length > 0) {
              // Sắp xếp theo order_number (tăng dần)
              matchingGroups.sort((a, b) => {
                const orderA = a.order_number ?? Number.MAX_SAFE_INTEGER;
                const orderB = b.order_number ?? Number.MAX_SAFE_INTEGER;
                return orderA - orderB;
              });

              const selectedGroup = matchingGroups[0];
              const maxNumber = await getMaxNumber(selectedGroup.id);
              const nextNumber = maxNumber + 1;

              setFormData(prev => {
                // Kiểm tra lại flag trước khi set
                if (userSelectedCatalogRef.current) {
                  return prev;
                }
                return {
                  ...prev,
                  docs_catalog_id: initialData.docs_catalog_id,
                  docs_group_id: selectedGroup.id,
                  docs_group_name: selectedGroup.name,
                  document_number: String(nextNumber)
                };
              });
              return;
            }
          }

          // Nếu chưa có loại văn bản trong initialData, tự động chọn "Công văn"
          // NHƯNG chỉ khi docs_catalog_id trong formData cũng chưa có giá trị (có thể đã được set từ document_code)
          const congVanCatalog = docsCatalogs.find(catalog => catalog.name === 'Công văn');
          if (congVanCatalog) {
            // QUAN TRỌNG: Kiểm tra xem docs_catalog_id đã được set từ document_code chưa
            // Nếu đã có giá trị (không phải 0), không ghi đè bằng "Công văn"
            if (currentCatalogId && currentCatalogId !== 0) {
              const existingCatalogName = docsCatalogs.find(c => c.id === currentCatalogId)?.name || 'unknown';
              return;
            }

            // Kiểm tra lại flag trước khi set
            if (userSelectedCatalogRef.current) {
              return;
            }

            // Lọc các sổ văn bản có catalog_ids chứa ID của "Công văn"
            const matchingGroups = docsGroups.filter(group => 
              group.is_active && 
              group.catalog_ids && 
              Array.isArray(group.catalog_ids) &&
              group.catalog_ids.includes(congVanCatalog.id)
            );

            if (matchingGroups.length > 0) {
              // Sắp xếp theo order_number (tăng dần)
              matchingGroups.sort((a, b) => {
                const orderA = a.order_number ?? Number.MAX_SAFE_INTEGER;
                const orderB = b.order_number ?? Number.MAX_SAFE_INTEGER;
                return orderA - orderB;
              });

              const selectedGroup = matchingGroups[0];
              // Lấy số max từ API (async)
              const maxNumber = await getMaxNumber(selectedGroup.id);
              const nextNumber = maxNumber + 1;

              setFormData(prev => {
                // Kiểm tra lại xem docs_catalog_id đã được set chưa (có thể đã thay đổi sau khi async call)
                if (prev.docs_catalog_id && prev.docs_catalog_id !== 0 && prev.docs_catalog_id !== congVanCatalog.id) {
                  const existingCatalogName = docsCatalogs.find(c => c.id === prev.docs_catalog_id)?.name || 'unknown';
                  return prev;
                }

                if (userSelectedCatalogRef.current) {
                  return prev;
                }

                
                return {
                  ...prev,
                  docs_catalog_id: congVanCatalog.id,
                  docs_group_id: selectedGroup.id,
                  docs_group_name: selectedGroup.name,
                  document_number: String(nextNumber)
                };
              });
            }
          }
        }
      }
    };

    autoSelectCatalogAndGroup();
  }, [open, mode, initialData, docsCatalogs, docsGroups, getMaxNumber, formData.document_number, isDocumentNumberLocked]);

  // Mặc định chọn "Công văn" khi tạo mới hoặc khi edit mà chưa có loại văn bản
  // Chỉ chạy khi dialog mới mở và chưa có loại văn bản được chọn
  useEffect(() => {
    const setDefaultCatalogAndGroup = async () => {
      
      // Chỉ xử lý cho create mode hoặc edit mode nhưng không có initialData hoặc đã có số văn bản
      if (open && docsCatalogs.length > 0 && docsGroups.length > 0) {
        // Kiểm tra xem người dùng đã chọn loại văn bản chưa
        if (userSelectedCatalogRef.current) {
          return;
        }
        
        // Sử dụng functional update để lấy giá trị mới nhất của formData và kiểm tra điều kiện
        setFormData(prev => {
          
          const docNumber = mode === 'edit' && initialData 
            ? String(initialData.document_number || '').trim() 
            : '';
          const currentDocNumber = String(prev.document_number || '').trim();
          const isLocked = isDocumentNumberLocked;
          
          
          // Chỉ xử lý khi chưa có loại văn bản và không phải trường hợp edit với số văn bản trống (đã xử lý ở useEffect trên)
          // VÀ không thay thế số văn bản nếu đã lock hoặc đã có số văn bản
          const condition1 = !prev.docs_catalog_id || prev.docs_catalog_id === 0;
          const condition2 = !(mode === 'edit' && initialData && (!docNumber || docNumber === ''));
          const condition3 = !(isLocked && currentDocNumber && currentDocNumber !== '');
          const shouldSetDefault = condition1 && condition2 && condition3;
          
          
          // Nếu đã có loại văn bản được chọn, không làm gì cả
          if (!shouldSetDefault) {
            return prev;
          }
          
          // Nếu điều kiện thỏa mãn, set default (sẽ lấy số sau)
          const congVanCatalog = docsCatalogs.find(catalog => catalog.name === 'Công văn');
          
          if (!congVanCatalog) {
            return prev;
          }
          
          // Lọc các sổ văn bản có catalog_ids chứa ID của "Công văn"
          const matchingGroups = docsGroups.filter(group => 
            group.is_active && 
            group.catalog_ids && 
            Array.isArray(group.catalog_ids) &&
            group.catalog_ids.includes(congVanCatalog.id)
          );


          // Sắp xếp theo order_number (tăng dần), nếu không có order_number thì đặt cuối
          matchingGroups.sort((a, b) => {
            const orderA = a.order_number ?? Number.MAX_SAFE_INTEGER;
            const orderB = b.order_number ?? Number.MAX_SAFE_INTEGER;
            return orderA - orderB;
          });

          // Chọn sổ có order_number nhỏ nhất
          const selectedGroup = matchingGroups.length > 0 ? matchingGroups[0] : undefined;

          if (selectedGroup) {
            
            // Kiểm tra lại flag trước khi set (có thể đã thay đổi trong quá trình async)
            if (userSelectedCatalogRef.current) {
              return prev;
            }
            
            // Nếu không bị lock, sẽ lấy số sau (async)
            if (!isLocked) {
              // Lấy số max từ API mới (async)
              getMaxNumber(selectedGroup.id).then(maxNumber => {
                const nextNumber = maxNumber + 1;
                
                setFormData(prevFormData => {
                  // Kiểm tra lại để đảm bảo chưa có loại văn bản được chọn bởi người dùng
                  if (userSelectedCatalogRef.current || (prevFormData.docs_catalog_id && prevFormData.docs_catalog_id !== 0 && prevFormData.docs_catalog_id !== congVanCatalog.id)) {
                    return prevFormData;
                  }
                  
                  
                  return {
                    ...prevFormData,
                    docs_catalog_id: congVanCatalog.id,
                    docs_group_id: selectedGroup.id,
                    docs_group_name: selectedGroup.name,
                    document_number: String(nextNumber)
                  };
                });
              });
              
              // Trả về giá trị tạm thời (sẽ được cập nhật sau)
              
              return {
                ...prev,
                docs_catalog_id: congVanCatalog.id,
                docs_group_id: selectedGroup.id,
                docs_group_name: selectedGroup.name
              };
            } else {
              
              return {
                ...prev,
                docs_catalog_id: congVanCatalog.id,
                docs_group_id: selectedGroup.id,
                docs_group_name: selectedGroup.name
              };
            }
          } else {
            
            return {
              ...prev,
              docs_catalog_id: congVanCatalog.id,
              docs_group_id: 0,
              docs_group_name: '',
              document_number: isLocked && currentDocNumber ? currentDocNumber : ''
            };
          }
        });
      } else {
      }
    };

    setDefaultCatalogAndGroup();
  }, [open, mode, docsCatalogs, docsGroups, getMaxNumber, initialData, isDocumentNumberLocked]);

  // Tự động chọn loại văn bản dựa trên document_code
  useEffect(() => {
    const autoSelectCatalogByCode = async () => {
      const docCode = String(formData.document_code || '').trim();
      const currentCatalogId = formData.docs_catalog_id;
      const currentDocNumber = formData.document_number;
      
      
      // Chỉ xử lý khi đã load xong catalogs và groups, dialog đang mở
      if (!open || docsCatalogs.length === 0 || docsGroups.length === 0) {
        return;
      }

      // Kiểm tra xem người dùng đã chọn loại văn bản chưa
      if (userSelectedCatalogRef.current) {
        return;
      }

      // Chỉ xử lý khi loại văn bản là 0 hoặc rỗng - ĐÂY LÀ ĐIỀU KIỆN QUAN TRỌNG
      if (currentCatalogId && currentCatalogId !== 0) {
        const catalogName = docsCatalogs.find(c => c.id === currentCatalogId)?.name || 'unknown';
        return;
      }

      // Chỉ xử lý khi document_code có giá trị
      if (!docCode) {
        return;
      }


      let targetCatalogName = 'Công văn'; // Mặc định
      const docCodeUpper = docCode.toUpperCase();

      
      if (docCodeUpper.includes('QĐ')) {
        targetCatalogName = 'Quyết định';
      } else if (docCodeUpper.includes('GM')) {
        targetCatalogName = 'Giấy mời';
      } else {
      }


      const targetCatalog = docsCatalogs.find(catalog => catalog.name === targetCatalogName);
      if (!targetCatalog) {
        return;
      }


      // Kiểm tra lại flag trước khi set (có thể đã thay đổi trong quá trình async)
      if (userSelectedCatalogRef.current) {
        return;
      }

      // Lọc các sổ văn bản có catalog_ids chứa ID của catalog được chọn
      const matchingGroups = docsGroups.filter(group =>
        group.is_active &&
        group.catalog_ids &&
        Array.isArray(group.catalog_ids) &&
        group.catalog_ids.includes(targetCatalog.id)
      );

      if (matchingGroups.length > 0) {
        // Sắp xếp theo order_number (tăng dần)
        matchingGroups.sort((a, b) => {
          const orderA = a.order_number ?? Number.MAX_SAFE_INTEGER;
          const orderB = b.order_number ?? Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        });

        const selectedGroup = matchingGroups[0];

        // Nếu không bị lock, sẽ lấy số sau (async)
        if (!isDocumentNumberLocked) {
          // Lấy số max từ API mới (async)
          getMaxNumber(selectedGroup.id).then(maxNumber => {
            const nextNumber = maxNumber + 1;

            setFormData(prevFormData => {
              // Kiểm tra lại để đảm bảo chưa có loại văn bản được chọn - QUAN TRỌNG
              if (userSelectedCatalogRef.current) {
                return prevFormData;
              }

              // Kiểm tra lại xem docs_catalog_id đã có giá trị hay chưa
              // QUAN TRỌNG: Nếu docs_catalog_id đã bằng targetCatalog.id (đã được set bởi chính logic này),
              // thì vẫn cho phép update document_number
              // Chỉ SKIP nếu docs_catalog_id khác với targetCatalog.id (đã bị thay đổi bởi logic khác)
              const prevCatalogId = prevFormData.docs_catalog_id;
              const prevDocNumber = prevFormData.document_number;
              
              if (prevCatalogId && prevCatalogId !== 0 && prevCatalogId !== targetCatalog.id) {
                const existingCatalogName = docsCatalogs.find(c => c.id === prevCatalogId)?.name || 'unknown';
                return prevFormData;
              }

              // Nếu docs_catalog_id đã bằng targetCatalog.id hoặc chưa có giá trị, cho phép update

              return {
                ...prevFormData,
                docs_catalog_id: targetCatalog.id,
                docs_group_id: selectedGroup.id,
                docs_group_name: selectedGroup.name,
                document_number: String(nextNumber)
              };
            });
          });

          // Trả về giá trị tạm thời (sẽ được cập nhật sau) - CẦN KIỂM TRA TRƯỚC KHI SET
          setFormData(prev => {
            // Kiểm tra lại xem docs_catalog_id đã có giá trị hay chưa
            const prevCatalogId = prev.docs_catalog_id;
            const prevDocNumber = prev.document_number;
            if (prevCatalogId && prevCatalogId !== 0) {
              return prev;
            }


            return {
              ...prev,
              docs_catalog_id: targetCatalog.id,
              docs_group_id: selectedGroup.id,
              docs_group_name: selectedGroup.name
            };
          });
        } else {

          setFormData(prev => {
            // Kiểm tra lại xem docs_catalog_id đã có giá trị hay chưa
            const prevCatalogId = prev.docs_catalog_id;
            const prevDocNumber = prev.document_number;
            if (prevCatalogId && prevCatalogId !== 0) {
              return prev;
            }


            return {
              ...prev,
              docs_catalog_id: targetCatalog.id,
              docs_group_id: selectedGroup.id,
              docs_group_name: selectedGroup.name
            };
          });
        }
      } else {

        setFormData(prev => {
          // Kiểm tra lại xem docs_catalog_id đã có giá trị hay chưa
          const prevCatalogId = prev.docs_catalog_id;
          const prevDocNumber = prev.document_number;
          if (prevCatalogId && prevCatalogId !== 0) {
            return prev;
          }


          return {
            ...prev,
            docs_catalog_id: targetCatalog.id,
            docs_group_id: 0,
            docs_group_name: ''
          };
        });
      }
      
    };

    autoSelectCatalogByCode();
  }, [formData.document_code, formData.docs_catalog_id, open, docsCatalogs, docsGroups, getMaxNumber, isDocumentNumberLocked]);

  useEffect(() => {
    // Nếu số văn bản thay đổi và khác với số ban đầu
    if (formData.document_number !== originalDocumentNumber) {
      // Reset validation state khi số văn bản thay đổi
      setNumberValid(null);
      setNumberError('');
      // Không tự động gọi API check number - chỉ check khi user click nút "Kiểm tra"
    } else if (formData.document_number === originalDocumentNumber && mode === 'edit') {
      // Nếu số văn bản giống với số ban đầu (khi edit), đánh dấu là hợp lệ
      setNumberValid(true);
      setNumberError('');
    }
  }, [formData.document_number, originalDocumentNumber, mode]);

  const loadDocsCatalogs = async () => {
    try {
      const catalogs = await fetchDocsCatalogs();
      setDocsCatalogs(catalogs);
    } catch (error) {
      console.error('Error fetching docs catalogs:', error);
      enqueueSnackbar('Không thể tải danh sách loại văn bản', { variant: 'error' });
    }
  };

  const loadDocsGroups = async () => {
    try {
      const response = await axiosInstance.get('/docs-groups', {
        params: { page_size: -1 }
      });
      if (response.data.code === 0) {
        setDocsGroups(response.data.data.records);
      } else {
        enqueueSnackbar('Không thể tải danh sách sổ văn bản', { variant: 'error' });
        setDocsGroups([]);
      }
    } catch (error) {
      console.error('Error loading docs groups from API:', error);
      enqueueSnackbar('Không thể tải danh sách sổ văn bản', { variant: 'error' });
      setDocsGroups([]);
    }
  };

  // Hàm lấy danh sách gợi ý sender
  const fetchSenderSuggestions = useCallback(async (keyword: string) => {
    if (!keyword || keyword.trim().length === 0) {
      setSenderSuggestions([]);
      return;
    }

    try {
      setSenderSuggestionsLoading(true);
      const params = new URLSearchParams();
      params.append('keyword', keyword.trim());
      params.append('limit', '20');
      
      const response = await axiosInstance.get(`/incoming-documents/sender-suggestions?${params.toString()}`);
      
      if (response.data.code === 0) {
        setSenderSuggestions(response.data.data.senders || []);
      } else {
        setSenderSuggestions([]);
      }
    } catch (error) {
      console.error('Error fetching sender suggestions:', error);
      setSenderSuggestions([]);
    } finally {
      setSenderSuggestionsLoading(false);
    }
  }, []);

  // Handler cho sender input change với debounce
  const handleSenderInputChange = (event: React.SyntheticEvent, newValue: string) => {
    setFormData({
      ...formData,
      sender: newValue
    });

    // Clear timeout cũ
    if (senderSearchTimeoutRef.current) {
      clearTimeout(senderSearchTimeoutRef.current);
    }

    // Debounce: chỉ gọi API sau 300ms khi user ngừng gõ
    senderSearchTimeoutRef.current = setTimeout(() => {
      fetchSenderSuggestions(newValue);
    }, 300);
  };

  // Handler khi chọn một option từ suggestions
  const handleSenderChange = (event: React.SyntheticEvent, newValue: string | null) => {
    setFormData({
      ...formData,
      sender: newValue || ''
    });
    setSenderSuggestions([]);
  };

  const handleSelectChange = (field: keyof typeof formData) => (event: SelectChangeEvent<any>) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };

  const handleTextFieldChange = (field: keyof typeof formData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    
    setFormData({
      ...formData,
      [field]: newValue
    });
  };

  const handleDateChange = (field: 'document_date' | 'received_date' | 'deadline') => (date: Date | null) => {
    if (date && !isNaN(date.getTime())) {
      const dateString = date.toISOString().split('T')[0];
      
      // Nếu thay đổi ngày đến
      if (field === 'received_date') {
        // Ở create mode: luôn tự động cập nhật deadline = received_date + 7
        // Ở edit mode: chỉ tự động cập nhật nếu deadline chưa có giá trị (rỗng)
        if (mode === 'create' || !formData.deadline || formData.deadline.trim() === '') {
          const deadlineDate = new Date(date);
          deadlineDate.setDate(deadlineDate.getDate() + 7);
          const deadlineString = deadlineDate.toISOString().split('T')[0];
          
          setFormData({
            ...formData,
            [field]: dateString,
            deadline: deadlineString
          });
        } else {
          // Ở edit mode và deadline đã có giá trị: chỉ cập nhật received_date, giữ nguyên deadline
          setFormData({
            ...formData,
            [field]: dateString
          });
        }
      } 
      // Nếu thay đổi ngày văn bản
      else if (field === 'document_date') {
        // Chỉ cập nhật document_date, không động đến deadline
        setFormData({
          ...formData,
          [field]: dateString
        });
      } else {
        // Khi thay đổi deadline trực tiếp: chỉ cập nhật deadline
        setFormData({
          ...formData,
          [field]: dateString
        });
      }
    } else {
      // Khi xóa date, set về chuỗi rỗng
      setFormData({
        ...formData,
        [field]: ''
      });
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;

    const files = Array.from(event.target.files);
    const newFiles: FileData[] = [];

    for (const file of files) {
      // Kiểm tra kích thước file
      if (file.size > MAX_FILE_SIZE) {
        enqueueSnackbar(`File ${file.name} vượt quá kích thước cho phép (${MAX_FILE_SIZE / (1024 * 1024)}MB)`, { variant: 'error' });
        continue;
      }

        newFiles.push({
          file_name: file.name,
          file_type: file.type,
          file_size: file.size,
          file_path: '',
          description: '',
          file_type_category: 'main',
        file: file
        });
    }

    setSelectedFiles([...selectedFiles, ...newFiles]);

    // Reset input để có thể chọn lại cùng một file
    event.target.value = '';
  };

  const handleSubmit = async (isDraft = false, action?: string) => {
    console.log('[handleSubmit] DEBUG document_code - Submit được gọi');
    console.log('[handleSubmit] DEBUG document_code - Final values:');
    console.log('[handleSubmit]   - document_code:', formData.document_code || '(rỗng)');
    console.log('[handleSubmit]   - docs_catalog_id:', formData.docs_catalog_id || '(rỗng/0)');
    console.log('[handleSubmit]   - docs_group_id:', formData.docs_group_id || '(rỗng/0)');
    console.log('[handleSubmit]   - document_number:', formData.document_number || '(rỗng)');
    console.log('[handleSubmit]   - isDraft:', isDraft, 'action:', action || '(không có)');
    
    if (!formData.document_date) {
      enqueueSnackbar('Vui lòng chọn ngày văn bản', { variant: 'error' });
      return;
    }
    if (!formData.received_date) {
      enqueueSnackbar('Vui lòng chọn ngày nhận văn bản', { variant: 'error' });
      return;
    }
    if (!formData.docs_group_name) {
      enqueueSnackbar('Vui lòng chọn sổ văn bản', { variant: 'error' });
      return;
    }
    if (!formData.description) {
      enqueueSnackbar('Vui lòng nhập trích yếu', { variant: 'error' });
      return;
    }
    if (!formData.sender) {
      enqueueSnackbar('Vui lòng nhập nơi gửi', { variant: 'error' });
      return;
    }
    
    // Kiểm tra file đính kèm (tính cả file bị xóa)
    const remainingExistingFiles = existingFiles.filter(file => !filesToDelete.includes(file.id));
    if (selectedFiles.length === 0 && remainingExistingFiles.length === 0) {
      enqueueSnackbar('Văn bản phải có ít nhất một file đính kèm', { variant: 'error' });
      return;
    }

    try {
      setLoading(true);
      setNumberError('');
      // Nếu số văn bản đã thay đổi và chưa check hợp lệ thì mới gọi API check
      if (formData.document_number !== originalDocumentNumber && numberValid !== true) {
        try {
          const checkResponse = await axiosInstance.post('/incoming-documents/check_number_avaiable', {
            document_number: formData.document_number,
            catalog_id: formData.docs_group_id,
            document_code: formData.document_code,
            id: initialData?.id || undefined
          });
          if (checkResponse.data.code !== 0) {
            setNumberValid(false);
            setNumberError(checkResponse.data.msg || 'Số văn bản không hợp lệ!');
            enqueueSnackbar(checkResponse.data.msg || 'Số văn bản không hợp lệ!', { variant: 'error' });
            setLoading(false);
            return;
          }
          setNumberValid(true);
        } catch (error: any) {
          setNumberValid(false);
          // Nếu có response và msg từ backend thì lấy msg đó, nếu không thì dùng msg mặc định
          const msg = error?.response?.data?.msg || 'Lỗi kiểm tra số văn bản!';
          setNumberError(msg);
          enqueueSnackbar(msg, { variant: 'error' });
          setLoading(false);
          return;
        }
      }

      // Xác định status dựa trên action
      let documentStatus = formData.status;
      if (isDraft) {
        documentStatus = 'draft';
      } else if (action) {
        switch (action) {
          case 'trinh_giam_doc':
            documentStatus = 'trinh_giam_doc';
            break;
          case 'trinh_pho_giam_doc':
            documentStatus = 'trinh_pho_giam_doc';
            break;
          case 'chuyen_tham_muu':
            documentStatus = 'chuyen_tham_muu';
            break;
          case 'chuyen_xu_ly':
            documentStatus = 'chuyen_xu_ly';
            break;
          default:
            documentStatus = formData.status;
        }
      }

      // Tạo/cập nhật văn bản trước
      const payload = {
        ...formData,
        status: documentStatus,
        files: [],
        files_to_delete: filesToDelete
      };
      
      console.log('[handleSubmit] DEBUG document_code - Payload được tạo:');
      console.log('[handleSubmit]   - payload.document_code:', payload.document_code || '(rỗng)');
      console.log('[handleSubmit]   - payload.docs_catalog_id:', payload.docs_catalog_id || '(rỗng/0)');
      console.log('[handleSubmit]   - payload.docs_group_id:', payload.docs_group_id || '(rỗng/0)');
      console.log('[handleSubmit]   - payload.document_number:', payload.document_number || '(rỗng)');

      // Kiểm tra trùng số ký hiệu trước khi tạo mới
      if (mode === 'create') {
        try {
          const dupResp = await axiosInstance.post('/incoming-documents/doc-num-confirm-by-code', {
            check_duplicate_code: true,
            docs_group_id: formData.docs_group_id,
            document_number: formData.document_number,
            document_code: formData.document_code
          });
          
          // Nếu trùng số ký hiệu/file (code === 10, 11, hoặc 12)
          if (dupResp?.data?.code === 10 || dupResp?.data?.code === 11 || dupResp?.data?.code === 12) {
            setDuplicateCodeData(dupResp.data.data);
            setDuplicateCodeType(dupResp.data.code);
            // Lưu payload và documentStatus để sử dụng khi người dùng xác nhận
            pendingUpdateRef.current = {
              payload,
              documentStatus,
              isDraft,
              action
            };
            setDuplicateCodeDialogOpen(true);
            setLoading(false);
            return; // Dừng lại, chờ người dùng xác nhận
          }
          
          // Nếu trùng số văn bản (code === 1) hoặc lỗi khác (không phải code 0, 10, 11, 12)
          if (dupResp?.data?.code !== 0 && dupResp?.data?.code !== 10 && dupResp?.data?.code !== 11 && dupResp?.data?.code !== 12) {
            enqueueSnackbar(dupResp?.data?.msg || 'Số văn bản không hợp lệ!', { variant: 'error' });
            setLoading(false);
            return; // Dừng lại, không tiếp tục gọi POST và không mở modal
          }
          
          // Chỉ tiếp tục nếu code === 0 (thành công) hoặc code === 10 (đã xử lý ở trên)
          // Nếu code === 0, tiếp tục gọi POST
        } catch (error: any) {
          // Nếu lỗi 400 với code 10, 11, hoặc 12 (trùng số ký hiệu / file / trùng ở sổ khác) → mở modal cảnh báo, cho phép tiếp tục
          if (error?.response?.data?.code === 10 || error?.response?.data?.code === 11 || error?.response?.data?.code === 12) {
            setDuplicateCodeData(error.response.data.data);
            setDuplicateCodeType(error.response.data.code);
            pendingUpdateRef.current = {
              payload,
              documentStatus,
              isDraft,
              action
            };
            setDuplicateCodeDialogOpen(true);
            setLoading(false);
            return;
          }
          // Nếu lỗi 400 với code 1 (trùng số văn bản) hoặc lỗi 400 khác
          if (error?.response?.status === 400) {
            const errorCode = error?.response?.data?.code;
            const errorMsg = error?.response?.data?.msg || 'Số văn bản không hợp lệ!';
            if (errorCode !== 10 && errorCode !== 11 && errorCode !== 12) {
              enqueueSnackbar(errorMsg, { variant: 'error' });
              setLoading(false);
              return;
            }
          } else {
            console.warn('Warning: doc-num-confirm-by-code API error (non-400):', error);
          }
        }
      }

      // Kiểm tra trùng số ký hiệu trước khi cập nhật (edit mode)
      if (mode === 'edit' && initialData?.id) {
        try {
          const confirmResponse = await axiosInstance.post(`/incoming-documents/${initialData.id}/doc-num-confirm`, {
            docs_group_id: formData.docs_group_id,
            document_number: formData.document_number,
            document_code: formData.document_code,
            check_duplicate_code: true
          });
          
          // Nếu trùng số ký hiệu/file (code === 10, 11, hoặc 12)
          if (confirmResponse?.data?.code === 10 || confirmResponse?.data?.code === 11 || confirmResponse?.data?.code === 12) {
            setDuplicateCodeData(confirmResponse.data.data);
            setDuplicateCodeType(confirmResponse.data.code);
            // Lưu payload và documentStatus để sử dụng khi người dùng xác nhận
            pendingUpdateRef.current = {
              payload,
              documentStatus,
              isDraft,
              action
            };
            setDuplicateCodeDialogOpen(true);
            setLoading(false);
            return; // Dừng lại, chờ người dùng xác nhận
          }
          
          // Nếu trùng số văn bản (code === 1) hoặc lỗi khác (không phải code 0, 10, 11, 12)
          if (confirmResponse?.data?.code !== 0 && confirmResponse?.data?.code !== 10 && confirmResponse?.data?.code !== 11 && confirmResponse?.data?.code !== 12) {
            enqueueSnackbar(confirmResponse?.data?.msg || 'Số văn bản không hợp lệ!', { variant: 'error' });
            setLoading(false);
            return; // Dừng lại, không tiếp tục gọi PUT/POST và không mở modal
          }
          
          // Chỉ tiếp tục nếu code === 0 (thành công)
          // Nếu code === 0, tiếp tục gọi PUT/POST
        } catch (error: any) {
          // Nếu lỗi 400 với code 10, 11, hoặc 12 (trùng số ký hiệu/file)
          if (error?.response?.data?.code === 10 || error?.response?.data?.code === 11 || error?.response?.data?.code === 12) {
            setDuplicateCodeData(error.response.data.data);
            setDuplicateCodeType(error.response.data.code);
            // Lưu payload và documentStatus để sử dụng khi người dùng xác nhận
            pendingUpdateRef.current = {
              payload,
              documentStatus,
              isDraft,
              action
            };
            setDuplicateCodeDialogOpen(true);
            setLoading(false);
            return;
          }
          // Nếu lỗi 400 với code 1 (trùng số văn bản) hoặc lỗi 400 khác
          if (error?.response?.status === 400) {
            const errorCode = error?.response?.data?.code;
            const errorMsg = error?.response?.data?.msg || 'Số văn bản không hợp lệ!';
            
            // Nếu không phải code 10, 11, 12, hiển thị lỗi và dừng
            if (errorCode !== 10 && errorCode !== 11 && errorCode !== 12) {
              enqueueSnackbar(errorMsg, { variant: 'error' });
              setLoading(false);
              return;
            }
            // Nếu là code 10, 11, hoặc 12, đã xử lý ở trên, không cần làm gì thêm
          } else {
            // Nếu không phải lỗi 400, bỏ qua và tiếp tục cập nhật như bình thường
            console.warn('Warning: doc-num-confirm API error (non-400):', error);
          }
        }
      }

      let response;
      if (mode === 'create') {
        response = await axiosInstance.post('/incoming-documents', payload);
      } else {
        response = await axiosInstance.put(`/incoming-documents/${initialData?.id}`, payload);
      }

      if (response.data.code !== 0) {
        throw new Error(response.data.message || `Có lỗi xảy ra khi ${mode === 'create' ? 'tạo' : 'cập nhật'} văn bản đến`);
      }

      // Kiểm tra và lấy documentId một cách an toàn
      let documentId: number;
      
      if (mode === 'create') {
        // Khi tạo mới, lấy ID từ response
        if (!response.data.data || !response.data.data.id) {
          throw new Error('Không nhận được ID văn bản từ server khi tạo mới');
        }
        documentId = response.data.data.id;
        const documentIdString = String(documentId);
        setCreatedDocumentId(documentIdString);
        // Lưu vào ref để sử dụng ngay lập tức
        createdDocumentIdRef.current = documentIdString;
        setDocumentCreated(true);
      } else {
        // Khi cập nhật, sử dụng ID từ initialData
        if (!initialData?.id) {
          throw new Error('Không có ID văn bản để cập nhật');
        }
        documentId = initialData.id;
        const documentIdString = String(documentId);
        setCreatedDocumentId(documentIdString);
        // Lưu vào ref để sử dụng ngay lập tức
        createdDocumentIdRef.current = documentIdString;
        setDocumentCreated(true);
      }

      // Upload files mới bằng phương pháp chunk
      let hasUploadError = false;
      const uploadedFiles: Array<{
        id: number;
        file_name: string;
        file_path: string;
        file_type: string;
        file_size: number;
        description: string;
      }> = [];
      
      if (selectedFiles.length > 0) {
        enqueueSnackbar('Đang xử lý file...', { variant: 'info' });
        
        for (const file of selectedFiles) {
          try {
            const uploadResult = await uploadFileWithChunks(
              documentId, 
              file, 
              formData,
              (progress) => {
                setUploadProgress(prev => ({
                  ...prev,
                  [file.file_name]: progress
                }));
              }
            );
            
            // Thêm file đã upload vào danh sách
            if (uploadResult && uploadResult.file_id) {
              uploadedFiles.push({
                id: uploadResult.file_id,
                file_name: file.file_name,
                file_path: uploadResult.file_path || '',
                file_type: file.file_type,
                file_size: file.file_size,
                description: file.description || ''
              });
            }
            
            enqueueSnackbar(`Upload file ${file.file_name} thành công`, { variant: 'success' });
          } catch (error) {
            console.error('Error uploading file:', error);
            enqueueSnackbar(`Lỗi khi upload file ${file.file_name}`, { variant: 'error' });
            hasUploadError = true;
          }
        }
        
        // Cập nhật existingFiles với các file vừa upload
        if (uploadedFiles.length > 0) {
          setExistingFiles(prev => [...prev, ...uploadedFiles]);
        }
      }

      // Chỉ đóng popup và gọi onSuccess khi không có lỗi upload
      if (!hasUploadError) {
        // Nếu có action từ dropdown, gọi API chi tiết và trả về để handleActionSelect mở popup
        if (action) {
          enqueueSnackbar(`${mode === 'create' ? 'Tạo' : 'Cập nhật'} văn bản thành công`, { variant: 'success' });
          
          // Gọi API lấy chi tiết văn bản để có dữ liệu đầy đủ cho popup
          let documentDetailData = null;
          try {
            const detailResponse = await axiosInstance.get('/xlvb/incoming-documents/chitiet-vanban', {
              params: { doc_id: documentId }
            });
            if (detailResponse.data.code === 0) {
              documentDetailData = detailResponse.data.data.document;
              setDocumentDetail(documentDetailData);
              // Lưu vào ref để sử dụng ngay lập tức
              documentDetailDataRef.current = documentDetailData;
            }
          } catch (error) {
            console.error('Error fetching document detail:', error);
            enqueueSnackbar('Không thể lấy chi tiết văn bản', { variant: 'warning' });
            // Nếu không lấy được chi tiết, tạo object từ formData
            documentDetailData = {
              id: documentId,
              ...formData,
              files: uploadedFiles.length > 0 ? uploadedFiles : existingFiles
            };
            setDocumentDetail(documentDetailData);
            // Lưu vào ref để sử dụng ngay lập tức
            documentDetailDataRef.current = documentDetailData;
          }
          
          // CHỈ đóng modal khi PUT/POST thành công và có documentDetailData
          // handleActionSelect sẽ mở modal action sau đó
          if (documentDetailData) {
            // Đóng modal CreateIncomingDocumentDialog
            handleClose();
          }
          
          // Trả về documentDetail để handleActionSelect có thể sử dụng
          return documentDetailData;
        }

        // Xử lý chuyển hướng dựa trên status của văn bản (chỉ khi không có action)
        if (mode === 'create' && response.data.data?.status) {
          const documentStatus = response.data.data.status;
          const currentPath = location.pathname;
          const currentSearch = location.search;
          
          if (documentStatus === 'draft') {
            const targetPath = '/qlvb/incoming-documents?submenu=van_ban_duthao';
            if (currentPath === '/qlvb/incoming-documents' && currentSearch.includes('submenu=van_ban_duthao')) {
              // Đang ở route đích, reload để nhận dữ liệu mới
              window.location.reload();
            } else {
              // Chuyển hướng đến route mới
              navigate(targetPath);
            }
          } else if (documentStatus === 'cho_xu_ly') {
            const targetPath = '/qlvb/incoming-documents?submenu=van_ban_cho_xu_ly';
            if (currentPath === '/qlvb/incoming-documents' && currentSearch.includes('submenu=van_ban_cho_xu_ly')) {
              // Đang ở route đích, reload để nhận dữ liệu mới
              window.location.reload();
            } else {
              // Chuyển hướng đến route mới
              navigate(targetPath);
            }
          }
        }
        
        // CHỈ đóng modal và gọi onSuccess khi PUT/POST thành công
        onSuccess();
        handleClose();
        enqueueSnackbar(`${mode === 'create' ? 'Tạo' : 'Cập nhật'} văn bản thành công`, { variant: 'success' });
      } else {
        enqueueSnackbar('Văn bản đã được tạo nhưng có lỗi khi upload một số file. Vui lòng thử lại.', { variant: 'warning' });
      }
    } catch (error: any) {
      console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} incoming document:`, error);
      
      // Xử lý lỗi từ API response
      let errorMessage = `Có lỗi xảy ra khi ${mode === 'create' ? 'tạo' : 'cập nhật'} văn bản đến`;
      
      if (error?.response?.data?.message) {
        // Lấy message từ API response
        errorMessage = error.response.data.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = [...selectedFiles];
    newFiles.splice(index, 1);
    setSelectedFiles(newFiles);
  };

  const handleDocsCatalogChange = async (event: SelectChangeEvent<any>) => {
    const catalogId = event.target.value;
    const currentDocumentNumber = formData.document_number;
    
    console.log('[handleDocsCatalogChange] START - catalogId:', catalogId, 'current docs_catalog_id:', formData.docs_catalog_id);
    
    // Đánh dấu rằng người dùng đã chọn loại văn bản
    userSelectedCatalogRef.current = true;
    
    setFormData(prev => {
      console.log('[handleDocsCatalogChange] setFormData - prev docs_catalog_id:', prev.docs_catalog_id, 'new catalogId:', catalogId);
      return {
        ...prev,
        docs_catalog_id: catalogId,
        docs_group_id: 0,
        docs_group_name: '',
        // Chỉ thay đổi số văn bản nếu không bị khóa
        document_number: isDocumentNumberLocked ? currentDocumentNumber : ''
      };
    });

    if (catalogId) {
      // Lọc các sổ văn bản có catalog_ids chứa catalogId được chọn
      const matchingGroups = docsGroups.filter(group => 
        group.is_active && 
        group.catalog_ids && 
        Array.isArray(group.catalog_ids) &&
        group.catalog_ids.includes(catalogId)
      );

      if (matchingGroups.length > 0) {
        // Sắp xếp theo order_number (tăng dần), nếu không có order_number thì đặt cuối
        matchingGroups.sort((a, b) => {
          const orderA = a.order_number ?? Number.MAX_SAFE_INTEGER;
          const orderB = b.order_number ?? Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        });

        // Chọn sổ có order_number nhỏ nhất
        const selectedGroup = matchingGroups[0];
        
        if (selectedGroup) {
          // Chỉ tự động cập nhật số văn bản nếu không bị khóa
          if (!isDocumentNumberLocked) {
            // Lấy số max từ API mới
            const maxNumber = await getMaxNumber(selectedGroup.id);
            const nextNumber = maxNumber + 1;

            setFormData(prev => {
              console.log('[handleDocsCatalogChange] setFormData (with number) - prev docs_catalog_id:', prev.docs_catalog_id, 'prev docs_group_id:', prev.docs_group_id, 'new docs_group_id:', selectedGroup!.id, 'new number:', nextNumber);
              return {
                ...prev,
                docs_group_id: selectedGroup!.id,
                docs_group_name: selectedGroup!.name,
                document_number: String(nextNumber)
              };
            });
          } else {
            console.log('[handleDocsCatalogChange] Setting group without new number (locked)');
            // Chỉ cập nhật sổ văn bản, giữ nguyên số văn bản
            setFormData(prev => {
              console.log('[handleDocsCatalogChange] setFormData (locked) - prev docs_catalog_id:', prev.docs_catalog_id, 'prev docs_group_id:', prev.docs_group_id, 'new docs_group_id:', selectedGroup!.id);
              return {
                ...prev,
                docs_group_id: selectedGroup!.id,
                docs_group_name: selectedGroup!.name
              };
            });
          }
        }
      } else {
        console.log('[handleDocsCatalogChange] No matching groups found for catalogId:', catalogId);
        enqueueSnackbar('Không tìm thấy sổ văn bản phù hợp với loại văn bản này', { variant: 'warning' });
      }
    }
    console.log('[handleDocsCatalogChange] END');
  };

  const handleDocsGroupChange = async (event: SelectChangeEvent<any>) => {
    const groupId = event.target.value;
    const selectedGroup = docsGroups.find(group => group.id === groupId && group.is_active);

    if (selectedGroup) {
      // Nếu đang ở edit mode và số văn bản rỗng, tự động bỏ lock để lấy số
      const docNumber = String(formData.document_number || '').trim();
      const shouldUnlock = mode === 'edit' && (!docNumber || docNumber === '');
      
      // Chỉ bỏ lock nếu số văn bản thực sự rỗng
      if (shouldUnlock) {
        setIsDocumentNumberLocked(false);
      }
      
      // Chỉ tự động cập nhật số văn bản nếu không bị khóa
      // Khi unlock: có thể update số văn bản khi chọn sổ văn bản khác
      // Khi lock: không được thay thế số văn bản
      const currentIsLocked = shouldUnlock ? false : isDocumentNumberLocked;
      const shouldUpdateNumber = !currentIsLocked;
      
      if (shouldUpdateNumber) {
        // Lấy số max từ API mới
        const maxNumber = await getMaxNumber(selectedGroup.id);
        const nextNumber = maxNumber + 1;

        setFormData(prev => ({
          ...prev,
          docs_group_id: groupId,
          docs_group_name: selectedGroup.name,
          document_number: String(nextNumber)
        }));
      } else {
        // Chỉ cập nhật sổ văn bản, giữ nguyên số văn bản
        setFormData(prev => ({
          ...prev,
          docs_group_id: groupId,
          docs_group_name: selectedGroup.name
        }));
      }

      // Sau khi chọn sổ, gọi API lấy danh sách số còn trống cho sổ này
      await fetchAvailableNumbersForGroup(selectedGroup.id);
    }
  };

  const handleRemoveExistingFile = (fileId: number) => {
    setFilesToDelete(prev => [...prev, fileId]);
    setExistingFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const handleCheckDocumentNumber = async () => {
    await checkDocumentNumber(formData.document_number, true);
  };

  const handleToggleDocumentNumberLock = async () => {
    const newLockState = !isDocumentNumberLocked;
    
    setIsDocumentNumberLocked(newLockState);

    // Nếu mở khóa và có sổ văn bản, ngay lập tức lấy số mới theo sổ văn bản đã được chọn
    if (!newLockState && formData.docs_group_id) {
      const maxNumber = await getMaxNumber(formData.docs_group_id);
      const nextNumber = maxNumber + 1;
      setFormData(prev => ({
        ...prev,
        document_number: String(nextNumber)
      }));
    }
  };

  const handleDropdownClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setDropdownOpen(true);
  };

  const handleDropdownClose = () => {
    setAnchorEl(null);
    setDropdownOpen(false);
  };

  // Kiểm tra validation tất cả các trường bắt buộc
  const validateForm = () => {
    if (!formData.document_date) {
      enqueueSnackbar('Vui lòng chọn ngày văn bản', { variant: 'error' });
      return false;
    }
    if (!formData.received_date) {
      enqueueSnackbar('Vui lòng chọn ngày nhận văn bản', { variant: 'error' });
      return false;
    }
    if (!formData.docs_group_name) {
      enqueueSnackbar('Vui lòng chọn sổ văn bản', { variant: 'error' });
      return false;
    }
    if (!formData.description) {
      enqueueSnackbar('Vui lòng nhập trích yếu', { variant: 'error' });
      return false;
    }
    if (!formData.sender) {
      enqueueSnackbar('Vui lòng nhập nơi gửi', { variant: 'error' });
      return false;
    }
    
    // Kiểm tra file đính kèm (tính cả file bị xóa)
    const remainingExistingFiles = existingFiles.filter(file => !filesToDelete.includes(file.id));
    if (selectedFiles.length === 0 && remainingExistingFiles.length === 0) {
      enqueueSnackbar('Văn bản phải có ít nhất một file đính kèm', { variant: 'error' });
      return false;
    }

    return true;
  };

  const handleActionSelect = async (action: string) => {
    handleDropdownClose();
    
    // Kiểm tra validation trước khi tạo văn bản
    if (!validateForm()) {
      return;
    }

    try {
      let documentDetailData = null;
      let submitSuccess = false;
      
      // Trong mode create: chỉ tạo văn bản nếu chưa được tạo
      // Trong mode edit: luôn cập nhật văn bản để lưu những thay đổi mới nhất
      if (mode === 'create' && !documentCreated) {
        documentDetailData = await handleSubmit(false, action);
        submitSuccess = true; // Nếu handleSubmit không throw error, nghĩa là thành công
      } else if (mode === 'edit') {
        documentDetailData = await handleSubmit(false, action);
        submitSuccess = true; // Nếu handleSubmit không throw error, nghĩa là thành công
      } else if (documentCreated && documentDetail) {
        // Nếu văn bản đã được tạo và có documentDetail, sử dụng nó
        documentDetailData = documentDetail;
        submitSuccess = true; // Văn bản đã được tạo thành công trước đó
      }
      
      // CHỈ mở modal khi PUT/POST thành công (submitSuccess = true)
      if (!submitSuccess || !documentDetailData) {
        console.warn('[handleActionSelect] Không thể mở modal vì PUT/POST chưa thành công');
        return; // Dừng lại, không mở modal
      }
      
      // Đảm bảo documentDetail được set trước khi mở modal
      setDocumentDetail(documentDetailData);
      // Lưu vào ref để sử dụng ngay lập tức
      documentDetailDataRef.current = documentDetailData;
      // Đợi một chút để state được cập nhật
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Sau khi tạo/cập nhật thành công, mở popup tương ứng
      switch (action) {
        case 'trinh_giam_doc':
          setTrinhGiamDocOpen(true);
          break;
        case 'trinh_pho_giam_doc':
          setTrinhPhoGiamDocOpen(true);
          break;
        case 'chuyen_tham_muu':
          // Lưu trạng thái modal "Tạo mới văn bản" trước khi mở modal "Chuyển tham mưu"
          wasCreateDialogOpenRef.current = open;
          // Đánh dấu cần mở lại dialog sau khi chuyển tham mưu thành công
          shouldReopenAfterActionRef.current = open;
          
          // Đảm bảo documentDetail và createdDocumentId đã được set
          console.log('[handleActionSelect] Mở modal "Chuyển tham mưu"');
          console.log('[handleActionSelect] createdDocumentId (state):', createdDocumentId);
          console.log('[handleActionSelect] createdDocumentId (ref):', createdDocumentIdRef.current);
          console.log('[handleActionSelect] documentDetail (state):', documentDetail);
          console.log('[handleActionSelect] documentDetailData:', documentDetailData);
          console.log('[handleActionSelect] documentDetailDataRef.current:', documentDetailDataRef.current);
          
          // Sử dụng ref nếu state chưa được cập nhật
          const finalDocumentId = createdDocumentId || createdDocumentIdRef.current;
          const finalDocumentDetail = documentDetail || documentDetailData || documentDetailDataRef.current;
          
          // Nếu có documentDetailData, set vào state và ref
          if (documentDetailData) {
            console.log('[handleActionSelect] Set documentDetail từ documentDetailData');
            setDocumentDetail(documentDetailData);
            documentDetailDataRef.current = documentDetailData;
            // Đảm bảo createdDocumentId cũng được set từ documentDetailData nếu có
            if (documentDetailData.id && !createdDocumentIdRef.current) {
              const docIdString = String(documentDetailData.id);
              setCreatedDocumentId(docIdString);
              createdDocumentIdRef.current = docIdString;
            }
            await new Promise(resolve => setTimeout(resolve, 100));
          }
          
          // Đảm bảo state được set trước khi đóng dialog
          if (finalDocumentDetail && !documentDetail) {
            setDocumentDetail(finalDocumentDetail);
          }
          if (finalDocumentId && !createdDocumentId) {
            setCreatedDocumentId(finalDocumentId);
          }
          
          // Đợi một chút để đảm bảo state đã được cập nhật
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Tạm thời đóng modal "Tạo mới văn bản" để hiển thị modal "Chuyển tham mưu"
          // Chỉ đóng dialog, không reset ref để giữ lại thông tin văn bản
          if (open) {
            // Reset formData nhưng giữ lại createdDocumentId và documentDetail
            setFormData({
              document_number: '',
              document_code: '',
              document_date: (() => {
                try {
                  return new Date().toISOString().split('T')[0];
                } catch (error) {
                  console.error('Error resetting document_date:', error);
                  return '';
                }
              })(),
              received_date: (() => {
                try {
                  return new Date().toISOString().split('T')[0];
                } catch (error) {
                  console.error('Error resetting received_date:', error);
                  return '';
                }
              })(),
              sender: '',
              docs_catalog_id: 0,
              docs_group_id: 0,
              docs_group_name: '',
              priority: 'Thường',
              security_level: 'Thường',
              description: '',
              status: 'cho_xu_ly',
              tags: [],
              deadline: (() => {
                try {
                  const receivedDate = new Date().toISOString().split('T')[0];
                  return getDefaultDeadline(receivedDate);
                } catch (error) {
                  return '';
                }
              })()
            });
            setSelectedFiles([]);
            setExistingFiles([]);
            setFilesToDelete([]);
            setCheckedDocumentNumber(false);
            setNumberValid(null);
            setNumberError('');
            setOriginalDocumentNumber('');
            setUploadProgress({});
            setAnchorEl(null);
            setDropdownOpen(false);
            setIsDocumentNumberLocked(true);
            // Gọi onClose để đóng dialog nhưng KHÔNG reset createdDocumentId và documentDetail
            onClose();
          }
          
          // Đợi một chút để dialog đóng hoàn toàn
          await new Promise(resolve => setTimeout(resolve, 200));
          
          // Kiểm tra lại trước khi mở modal - sử dụng ref vì state có thể đã bị reset
          const finalDocId = createdDocumentIdRef.current || createdDocumentId;
          const finalDocDetail = documentDetailDataRef.current || documentDetail;
          console.log('[handleActionSelect] Trước khi mở modal - createdDocumentId:', finalDocId, 'documentDetail:', finalDocDetail ? 'có' : 'không có');
          
          // Đảm bảo state được set lại từ ref trước khi mở modal
          if (finalDocDetail && !documentDetail) {
            setDocumentDetail(finalDocDetail);
          }
          if (finalDocId && !createdDocumentId) {
            setCreatedDocumentId(finalDocId);
          }
          
          setChuyenThamMuuOpen(true);
          break;
        case 'chuyen_xu_ly':
          setChuyenXuLyOpen(true);
          break;
      }
    } catch (error) {
      console.error('Error creating/updating document:', error);
      // Lỗi đã được xử lý trong handleSubmit
      // KHÔNG mở modal khi có lỗi
      // KHÔNG đóng modal CreateIncomingDocumentDialog khi có lỗi
      return;
    }
  };

  const handleClose = () => {
    // Clear timeout nếu có
    if (senderSearchTimeoutRef.current) {
      clearTimeout(senderSearchTimeoutRef.current);
    }
    
    setFormData({
      document_number: '',
      document_code: '',
      document_date: (() => {
        try {
          return new Date().toISOString().split('T')[0];
        } catch (error) {
          console.error('Error resetting document_date:', error);
          return '';
        }
      })(),
      received_date: (() => {
        try {
          return new Date().toISOString().split('T')[0];
        } catch (error) {
          console.error('Error resetting received_date:', error);
          return '';
        }
      })(),
      sender: '',
      docs_catalog_id: 0,
      docs_group_id: 0,
      docs_group_name: '',
      priority: 'Thường',
      security_level: 'Thường',
      description: '',
      status: 'cho_xu_ly',
      tags: [],
      deadline: getDefaultDeadline()
    });
    setSelectedFiles([]);
    setExistingFiles([]);
    setFilesToDelete([]);
    setCheckedDocumentNumber(false);
    setNumberValid(null);
    setNumberError('');
    setOriginalDocumentNumber('');
    setUploadProgress({});
    setAnchorEl(null);
    setDropdownOpen(false);
    setTrinhGiamDocOpen(false);
    setTrinhPhoGiamDocOpen(false);
    setChuyenThamMuuOpen(false);
    setChuyenXuLyOpen(false);
    setCreatedDocumentId(null);
    setDocumentCreated(false);
    setDocumentDetail(null);
    documentDetailDataRef.current = null;
    createdDocumentIdRef.current = null;
    wasCreateDialogOpenRef.current = false;
    shouldReopenAfterActionRef.current = false;
    setIsDocumentNumberLocked(true); // Reset lock state
    setSenderSuggestions([]); // Reset sender suggestions
    setSenderSuggestionsLoading(false); // Reset loading state
    onClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale} localeText={pickerLocaleText}>
      <Dialog
        open={open}
        onClose={loading ? undefined : handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh' }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: '#086428',
          color: 'white',
          fontWeight: 500,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: '51px',
          padding: '12px 16px',
          boxSizing: 'border-box'
        }}>
          {mode === 'create' ? 'Thêm văn bản đến mới' : 'Cập nhật văn bản đến'}
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleClose}
            disabled={loading}
            aria-label="close"
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Grid container spacing={2}>
            {/* Số ký hiệu, Số văn bản, Ngày văn bản, Ngày nhận - 4 trường trên 1 dòng */}
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Số ký hiệu"               
                value={formData.document_code}
                onChange={handleTextFieldChange('document_code')}
                size="small"
              />
            </Grid>
            <Grid item xs={3}>
            <TextField
              fullWidth
              label="Số văn bản"
              value={formData.document_number}
              onChange={handleTextFieldChange('document_number')}
              size="small"
              InputProps={{
                endAdornment: (
                  <>
                    {/* Icon Lock/Unlock chỉ hiển thị khi ở edit mode */}
                    {mode === 'edit' && (
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={handleToggleDocumentNumberLock}
                        title={isDocumentNumberLocked ? 'Mở khóa để tự động cập nhật số văn bản' : 'Khóa số văn bản'}
                        sx={{ mr: 0.5 }}
                      >
                        {isDocumentNumberLocked ? (
                          <LockIcon fontSize="small" />
                        ) : (
                          <LockOpenIcon fontSize="small" />
                        )}
                      </IconButton>
                    )}
                    {/* Icon Check hoặc Checkbox */}
                    {(formData.document_number === originalDocumentNumber || numberValid === true) ? (
                      <IconButton edge="end" size="small" disabled>
                        <CheckCircle style={{ color: 'green' }} />
                      </IconButton>
                    ) : (
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={handleCheckDocumentNumber}
                        disabled={checkingNumber}
                      >
                        <Checkbox
                          checked={false}
                          indeterminate={checkingNumber}
                          style={{ padding: 0 }}
                        />
                      </IconButton>  
                    )}
                  </>
                ),
              }}
              error={!!numberError}
              helperText={numberError}
              sx={{
                '& .MuiInputBase-input': {
                  fontWeight: 'bold',
                  color: '#d32f2f !important'
                }
              }}
            />
            </Grid>
            <Grid item xs={3}>
              <DatePicker
                label="Ngày văn bản"
                value={formData.document_date && formData.document_date.trim() !== '' ? new Date(formData.document_date) : null}
                onChange={handleDateChange('document_date')}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    size: 'small',
                    required: true,
                    fullWidth: true
                  }
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <DatePicker
                label="Ngày nhận"
                value={formData.received_date && formData.received_date.trim() !== '' ? new Date(formData.received_date) : null}
                onChange={handleDateChange('received_date')}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    size: 'small',
                    required: true,
                    fullWidth: true
                  }
                }}
              />
            </Grid>

            {/* Hàng hiển thị các số còn trống trong sổ hiện tại (ngay dưới hàng đầu tiên) */}
            <Grid item xs={12}>
              {formData.docs_group_id !== 0 && (
                <Tooltip
                  title={
                    availableNumbers && availableNumbers.length > 0
                      ? `Các số còn trống: ${availableNumbers.join(', ')}`
                      : 'Không có số trống trong khoảng'
                  }
                  arrow
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: availableNumbers.length > 0 ? '#086428' : 'text.secondary',
                      cursor: 'default',
                    }}
                  >
                    {loadingAvailableNumbers
                      ? 'Đang tải các số còn trống...'
                      : availableNumbers.length === 0
                        ? 'Không có số trống trong khoảng'
                        : (() => {
                            const firstFive = availableNumbers.slice(0, 5).join(', ');
                            const hasMore = availableNumbers.length > 5;
                            return `Số trống trong sổ hiện tại: ${firstFive}${hasMore ? ', ...' : ''}`;
                          })()}
                  </Typography>
                </Tooltip>
              )}
            </Grid>

            {/* Nơi gửi */}
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                options={senderSuggestions}
                value={formData.sender}
                onInputChange={handleSenderInputChange}
                onChange={handleSenderChange}
                loading={senderSuggestionsLoading}
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Nơi gửi *"
                    fullWidth
                  />
                )}
                noOptionsText="Không tìm thấy gợi ý"
                loadingText="Đang tải..."
              />
            </Grid>

            {/* Sổ văn bản, Độ khẩn - 2 trường trên 1 dòng */}
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Loại văn bản</InputLabel>
                <Select
                  value={formData.docs_catalog_id}
                  label="Loại văn bản"
                  onChange={handleDocsCatalogChange}
                >
                  <MenuItem value={0}>Chọn loại văn bản</MenuItem>
                  {docsCatalogs.map((catalog) => (
                    <MenuItem key={catalog.id} value={catalog.id}>
                      {catalog.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Sổ văn bản *</InputLabel>
                <Select
                  value={formData.docs_group_id}
                  onChange={handleDocsGroupChange}
                  label="Sổ văn bản *"
                >
                  <MenuItem value={0}>Chọn sổ văn bản</MenuItem>
                  {docsGroupsForSelect.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Độ khẩn</InputLabel>
                <Select
                  value={formData.priority}
                  onChange={handleSelectChange('priority')}
                  label="Độ khẩn"
                >
                  {PRIORITY_LEVELS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Độ mật, Hạn xử lý và Tags - 3 trường trên 1 dòng */}
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Độ mật</InputLabel>
                <Select
                  value={formData.security_level}
                  onChange={handleSelectChange('security_level')}
                  label="Độ mật"
                >
                  {SECURITY_LEVELS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <DatePicker
                label="Hạn xử lý"
                value={formData.deadline && formData.deadline.trim() !== '' ? new Date(formData.deadline) : null}
                onChange={handleDateChange('deadline')}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true
                  }
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Tags"
                placeholder="Nhập và nhấn Enter để thêm tag"
                size="small"
                inputProps={{
                  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter' && e.currentTarget && e.currentTarget.value) {
                      e.preventDefault();
                      const inputValue = e.currentTarget.value.trim();
                      if (inputValue && !formData.tags.includes(inputValue)) {
                        setFormData({
                          ...formData,
                          tags: [...formData.tags, inputValue]
                        });
                      }
                      e.currentTarget.value = '';
                    }
                  }
                }}
              />
              {formData.tags.length > 0 && (
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {formData.tags.map((tag, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        bgcolor: '#f0f0f0',
                        borderRadius: '16px',
                        px: 1,
                        py: 0.5,
                        fontSize: '0.8125rem'
                      }}
                    >
                      {tag}
                      <IconButton
                        size="small"
                        onClick={() => {
                          const newTags = [...formData.tags];
                          newTags.splice(index, 1);
                          setFormData({ ...formData, tags: newTags });
                        }}
                        sx={{ ml: 0.5, p: 0.25 }}
                      >
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Grid>

            {/* Trích yếu */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Box sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2">Trích yếu</Typography>
                <Typography variant="body2" color="error" sx={{ ml: 0.5 }}>*</Typography>
              </Box>
              <Box
                sx={{
                  border: '1px solid #c4c4c4',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  '&:hover': {
                    border: '1px solid #000',
                  },
                  '&:focus-within': {
                    border: '2px solid #1976d2',
                    margin: '-1px',
                  },
                }}
              >
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  style={{
                    width: '100%',
                    minHeight: '110px',
                    padding: '16.5px 14px',
                    fontFamily: 'inherit',
                    fontSize: 'inherit',
                    border: 'none',
                    outline: 'none',
                    resize: 'vertical',
                  }}
                  placeholder="Nhập trích yếu"
                />
              </Box>
            </Grid>

            {/* File đính kèm */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                File đính kèm
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<AddIcon />}
                    disabled={loading}
                  >
                    Thêm file
                    <input
                      type="file"
                      hidden
                      multiple
                      onChange={handleFileChange}
                    />
                  </Button>
                </Box>

                {/* Danh sách file hiện có */}
                {existingFiles.length > 0 && (
                  <List dense>
                    {existingFiles.map((file) => (
                      <ListItem key={file.id}>
                        <ListItemText
                          primary={file.file_name}
                          secondary={formatFileSize(file.file_size)}
                        />
                        <ListItemSecondaryAction>
                          <IconButton edge="end" onClick={() => handleRemoveExistingFile(file.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}

                {/* Danh sách file mới thêm */}
                {selectedFiles.length > 0 && (
                  <List dense>
                    {selectedFiles.map((file, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={file.file_name}
                          secondary={
                            <>
                              {formatFileSize(file.file_size)}
                              {loading && uploadProgress[file.file_name] !== undefined && (
                                <Box sx={{ mt: 1 }}>
                                  <LinearProgress 
                                    variant="determinate" 
                                    value={uploadProgress[file.file_name]} 
                                    sx={{ height: 6, borderRadius: 3 }}
                                  />
                                  <Typography variant="caption" color="textSecondary">
                                    {Math.round(uploadProgress[file.file_name])}%
                                  </Typography>
                                </Box>
                              )}
                            </>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton edge="end" onClick={() => handleRemoveFile(index)}>
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          {/* Chỉ hiển thị nút "Lưu để theo dõi" khi trạng thái phù hợp */}
          {['', 'draft', 'cho_xu_ly'].includes(formData.status) && (
            <Box>
              <Button
                variant="outlined"
                onClick={() => handleSubmit(true)}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
                sx={{ mr: 1, textWrap: 'nowrap' }}
              >
                Lưu để theo dõi
              </Button>
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'flex-end' }}>
            <Button
              onClick={handleClose}
              disabled={loading}
              sx={{ mr: 1 }}
            >
              Hủy
            </Button>
            {/* Hiển thị nút "Lưu" trong các trường hợp khác */}
            {!['', 'draft', 'cho_xu_ly'].includes(formData.status) && (
              <Button
                variant="contained"
                onClick={() => handleSubmit(false)}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
                sx={{ mr: 1 }}
              >
                Lưu
              </Button>
            )}
            {/* Chỉ hiển thị nút "Chuyển" khi trạng thái phù hợp */}
            {['', 'draft', 'cho_xu_ly'].includes(formData.status) && (
              <Button
                variant="contained"
                onClick={handleDropdownClick}
                disabled={loading}
                endIcon={<ArrowDropDownIcon />}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                Chuyển
              </Button>
            )}
            <Menu
              anchorEl={anchorEl}
              open={dropdownOpen}
              onClose={handleDropdownClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={() => handleActionSelect('trinh_giam_doc')}>
                Trình giám đốc
              </MenuItem>
              <MenuItem onClick={() => handleActionSelect('trinh_pho_giam_doc')}>
                Trình Phó giám đốc
              </MenuItem>
              <MenuItem onClick={() => handleActionSelect('chuyen_tham_muu')}>
                Chuyển tham mưu
              </MenuItem>
              <MenuItem onClick={() => handleActionSelect('chuyen_xu_ly')}>
                Chuyển xử lý
              </MenuItem>
            </Menu>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Popup Trình giám đốc */}
      <TrinhGiamDocPopup
        open={trinhGiamDocOpen}
        onClose={() => setTrinhGiamDocOpen(false)}
        documentId={createdDocumentId ?? undefined}
        documentData={documentDetail}
        action="qlvb/incoming-documents/trinh-giamdoc"
        onActionSuccess={() => {
          setTrinhGiamDocOpen(false);
          onSuccess();
          handleClose();
        }}
      />

      {/* Popup Trình phó giám đốc */}
      <TrinhPhoGiamDocPopup
        open={trinhPhoGiamDocOpen}
        onClose={() => setTrinhPhoGiamDocOpen(false)}
        documentId={createdDocumentId ?? undefined}
        documentData={documentDetail}
        action="qlvb/incoming-documents/trinh-pho-giamdoc"
        onActionSuccess={() => {
          setTrinhPhoGiamDocOpen(false);
          onSuccess();
          handleClose();
        }}
      />

      {/* Popup Chuyển tham mưu */}
      <ChuyenThamMuuPopup
        open={chuyenThamMuuOpen}
        onClose={() => {
          setChuyenThamMuuOpen(false);
          // Nếu trước đó modal "Tạo mới văn bản" đang mở, mở lại
          if (wasCreateDialogOpenRef.current) {
            console.log('[ChuyenThamMuuPopup] onClose - Mở lại modal "Tạo mới văn bản"');
            // Gọi onSuccess để parent có thể mở lại modal "Tạo mới văn bản"
            // onSuccess() sẽ được parent xử lý để mở lại modal nếu cần
            onSuccess();
          }
          // Reset ref
          wasCreateDialogOpenRef.current = false;
        }}
        documentId={(createdDocumentId || createdDocumentIdRef.current) ?? undefined}
        documentData={documentDetail || documentDetailDataRef.current}
        action="qlvb/incoming-documents/chuyen-tham-muu"
        onActionSuccess={() => {
          setChuyenThamMuuOpen(false);
          
          // Kiểm tra xem có cần mở lại modal "Tạo mới văn bản" không
          const shouldReopen = shouldReopenAfterActionRef.current;
          console.log('[ChuyenThamMuuPopup] onActionSuccess - shouldReopen:', shouldReopen);
          
          // Reset ref và data
          wasCreateDialogOpenRef.current = false;
          documentDetailDataRef.current = null;
          createdDocumentIdRef.current = null;
          
          if (shouldReopen) {
            console.log('[ChuyenThamMuuPopup] onActionSuccess - Mở lại modal "Tạo mới văn bản"');
            const modalId = 'create-incoming-document';
            const hasCallback = modalReopenManager.hasCallback(modalId);
            console.log('[ChuyenThamMuuPopup] onActionSuccess - modalReopenManager.hasCallback:', hasCallback);
            
            // Reset flag
            shouldReopenAfterActionRef.current = false;
            
            // Reset form để người dùng có thể tạo văn bản mới
            setFormData({
              document_number: '',
              document_code: '',
              document_date: (() => {
                try {
                  return new Date().toISOString().split('T')[0];
                } catch (error) {
                  console.error('Error resetting document_date:', error);
                  return '';
                }
              })(),
              received_date: (() => {
                try {
                  return new Date().toISOString().split('T')[0];
                } catch (error) {
                  console.error('Error resetting received_date:', error);
                  return '';
                }
              })(),
              sender: '',
              docs_catalog_id: 0,
              docs_group_id: 0,
              docs_group_name: '',
              priority: 'Thường',
              security_level: 'Thường',
              description: '',
              status: 'cho_xu_ly',
              tags: [],
              deadline: (() => {
                try {
                  const receivedDate = new Date().toISOString().split('T')[0];
                  return getDefaultDeadline(receivedDate);
                } catch (error) {
                  return '';
                }
              })()
            });
            setSelectedFiles([]);
            setExistingFiles([]);
            setFilesToDelete([]);
            setCheckedDocumentNumber(false);
            setNumberValid(null);
            setNumberError('');
            setOriginalDocumentNumber('');
            setUploadProgress({});
            setAnchorEl(null);
            setDropdownOpen(false);
            setCreatedDocumentId(null);
            setDocumentCreated(false);
            setDocumentDetail(null);
            setIsDocumentNumberLocked(true);
            
            // Gọi onSuccess để parent refresh data (sẽ đóng dialog)
            onSuccess();
            
            // Đợi một chút để dialog đóng hoàn toàn, rồi mở lại
            setTimeout(() => {
              if (hasCallback) {
                console.log('[ChuyenThamMuuPopup] onActionSuccess - Gọi modalReopenManager.reopen() để mở lại dialog');
                modalReopenManager.reopen(modalId);
              } else {
                console.log('[ChuyenThamMuuPopup] onActionSuccess - Không có callback được đăng ký, không thể mở lại dialog');
              }
            }, 300);
          } else {
            // Nếu không cần mở lại, đóng dialog hoàn toàn
            handleClose();
          }
        }}
      />

      {/* Popup Chuyển xử lý */}
      <ChuyenXuLyPopup
        open={chuyenXuLyOpen}
        onClose={() => setChuyenXuLyOpen(false)}
        documentId={createdDocumentId ?? undefined}
        documentData={documentDetail}
        action="/api/xlvb/incoming-documents/chuyen_xu_ly"
        onSuccess={() => {
          setChuyenXuLyOpen(false);
          onSuccess();
          handleClose();
        }}
      />

      {/* Dialog cảnh báo trùng số ký hiệu/file */}
      <Dialog
        open={duplicateCodeDialogOpen}
        onClose={() => {
          setDuplicateCodeDialogOpen(false);
          setDuplicateCodeData(null);
          setDuplicateCodeType(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#fff3e0', color: '#e65100' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {duplicateCodeType === 10 && 'Cảnh báo: Số ký hiệu đã tồn tại'}
              {duplicateCodeType === 11 && 'Cảnh báo: File đã tồn tại'}
              {duplicateCodeType === 12 && 'Cảnh báo: Số ký hiệu đã tồn tại ở sổ văn bản khác'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {/* Code = 10: Trùng số ký hiệu trong cùng sổ */}
          {duplicateCodeType === 10 && (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Số ký hiệu "{formData.document_code}" đã tồn tại trong sổ văn bản "{duplicateCodeData?.docs_group_name || formData.docs_group_name}".
              </Typography>
              {mode === 'create' && (
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>
                  Bạn đang tạo mới văn bản với số ký hiệu đã tồn tại.
                </Typography>
              )}
              {duplicateCodeData && (
                <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1, mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Thông tin văn bản đã tồn tại:
                  </Typography>
                  <Typography variant="body2">
                    <strong>Số văn bản:</strong> {duplicateCodeData.document_number}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Số ký hiệu:</strong> {duplicateCodeData.document_code}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Sổ văn bản:</strong> {duplicateCodeData.docs_group_name}
                  </Typography>
                </Box>
              )}
            </>
          )}

          {/* Code = 11: File trùng */}
          {duplicateCodeType === 11 && (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {duplicateCodeData?.msg || 'Các file chính của văn bản đã tồn tại trong hệ thống.'}
              </Typography>
              {duplicateCodeData?.duplicate_documents && duplicateCodeData.duplicate_documents.length > 0 && (
                <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1, mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Tìm thấy {duplicateCodeData.duplicate_count || duplicateCodeData.duplicate_documents.length} văn bản có file trùng:
                  </Typography>
                  <List dense>
                    {duplicateCodeData.duplicate_documents.map((doc: any, index: number) => (
                      <ListItem key={index} sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 1 }}>
                        <Typography variant="body2">
                          <strong>Số văn bản:</strong> {doc.document_number}
                        </Typography>
                        {doc.document_code && (
                          <Typography variant="body2">
                            <strong>Số ký hiệu:</strong> {doc.document_code}
                          </Typography>
                        )}
                        <Typography variant="body2">
                          <strong>Trích yếu:</strong> {doc.title}
                        </Typography>
                        {doc.docs_group_name && (
                          <Typography variant="body2">
                            <strong>Sổ văn bản:</strong> {doc.docs_group_name}
                          </Typography>
                        )}
                      </ListItem>
                    ))}
                  </List>
                  {duplicateCodeData?.duplicate_files && duplicateCodeData.duplicate_files.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Các file trùng:
                      </Typography>
                      {duplicateCodeData.duplicate_files.map((file: any, index: number) => (
                        <Typography key={index} variant="body2" sx={{ pl: 2 }}>
                          • {file.file_name}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}

          {/* Code = 12: Trùng số ký hiệu ở sổ văn bản khác */}
          {duplicateCodeType === 12 && (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {duplicateCodeData?.msg || `Số ký hiệu "${formData.document_code}" đã tồn tại trong sổ văn bản khác.`}
              </Typography>
              {duplicateCodeData && (
                <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1, mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Thông tin sổ văn bản:
                  </Typography>
                  <Typography variant="body2">
                    <strong>Sổ văn bản hiện tại:</strong> {duplicateCodeData.docs_group_name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Sổ văn bản có số ký hiệu trùng:</strong> {duplicateCodeData.other_docs_group_name} (ID: {duplicateCodeData.other_docs_group_id})
                  </Typography>
                  {duplicateCodeData.duplicate_document_id && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>ID văn bản trùng:</strong> {duplicateCodeData.duplicate_document_id}
                    </Typography>
                  )}
                </Box>
              )}
            </>
          )}

          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
            Bạn có muốn tiếp tục {mode === 'create' ? 'tạo mới' : 'cập nhật'} văn bản này không?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setDuplicateCodeDialogOpen(false);
              setDuplicateCodeData(null);
              setDuplicateCodeType(null);
            }}
            variant="outlined"
          >
            Hủy
          </Button>
          <Button
            onClick={async () => {
              setDuplicateCodeDialogOpen(false);
              setDuplicateCodeType(null);
              // Tiếp tục gọi API để tạo mới hoặc cập nhật
              if (!pendingUpdateRef.current) {
                setLoading(false);
                return;
              }
              
              try {
                setLoading(true);
                const { payload: savedPayload, isDraft: savedIsDraft, action: savedAction } = pendingUpdateRef.current;
                
                let response;
                if (mode === 'create') {
                  response = await axiosInstance.post('/incoming-documents', savedPayload);
                } else {
                  response = await axiosInstance.put(`/incoming-documents/${initialData?.id}`, savedPayload);
                }
                
                if (response.data.code !== 0) {
                  throw new Error(response.data.message || `Có lỗi xảy ra khi ${mode === 'create' ? 'tạo' : 'cập nhật'} văn bản đến`);
                }
                
                // Tiếp tục xử lý như bình thường (upload files, etc.)
                let documentId: number;
                
                if (mode === 'create') {
                  // Khi tạo mới, lấy ID từ response
                  if (!response.data.data || !response.data.data.id) {
                    throw new Error('Không nhận được ID văn bản từ server khi tạo mới');
                  }
                  documentId = response.data.data.id;
                } else {
                  // Khi cập nhật, sử dụng ID từ initialData
                  if (!initialData?.id) {
                    throw new Error('Không có ID văn bản để cập nhật');
                  }
                  documentId = initialData.id;
                }
                
                const documentIdString = String(documentId);
                setCreatedDocumentId(documentIdString);
                createdDocumentIdRef.current = documentIdString;
                setDocumentCreated(true);
                
                // Upload files mới bằng phương pháp chunk
                let hasUploadError = false;
                const uploadedFiles: Array<{
                  id: number;
                  file_name: string;
                  file_path: string;
                  file_type: string;
                  file_size: number;
                  description: string;
                }> = [];
                
                if (selectedFiles.length > 0) {
                  enqueueSnackbar('Đang xử lý file...', { variant: 'info' });
                  
                  for (const file of selectedFiles) {
                    try {
                      const uploadResult = await uploadFileWithChunks(
                        documentId, 
                        file, 
                        formData,
                        (progress) => {
                          setUploadProgress(prev => ({
                            ...prev,
                            [file.file_name]: progress
                          }));
                        }
                      );
                      
                      if (uploadResult && uploadResult.file_id) {
                        uploadedFiles.push({
                          id: uploadResult.file_id,
                          file_name: file.file_name,
                          file_path: uploadResult.file_path || '',
                          file_type: file.file_type,
                          file_size: file.file_size,
                          description: file.description || ''
                        });
                      }
                      
                      enqueueSnackbar(`Upload file ${file.file_name} thành công`, { variant: 'success' });
                    } catch (uploadError: any) {
                      console.error('Error uploading file:', uploadError);
                      hasUploadError = true;
                      enqueueSnackbar(`Lỗi khi upload file ${file.file_name}`, { variant: 'error' });
                    }
                  }
                  
                  // Cập nhật existingFiles với các file vừa upload
                  if (uploadedFiles.length > 0) {
                    setExistingFiles(prev => [...prev, ...uploadedFiles]);
                  }
                }
                
                // Xử lý action nếu có
                if (savedAction) {
                  enqueueSnackbar(`${mode === 'create' ? 'Tạo' : 'Cập nhật'} văn bản thành công`, { variant: 'success' });
                  
                  // Gọi API lấy chi tiết văn bản để có dữ liệu đầy đủ cho popup
                  let documentDetailData = null;
                  try {
                    const detailResponse = await axiosInstance.get('/xlvb/incoming-documents/chitiet-vanban', {
                      params: { doc_id: documentId }
                    });
                    if (detailResponse.data.code === 0) {
                      documentDetailData = detailResponse.data.data.document;
                      setDocumentDetail(documentDetailData);
                      documentDetailDataRef.current = documentDetailData;
                    }
                  } catch (error) {
                    console.error('Error fetching document detail:', error);
                    documentDetailData = {
                      id: documentId,
                      ...formData,
                      files: uploadedFiles.length > 0 ? uploadedFiles : existingFiles
                    };
                    setDocumentDetail(documentDetailData);
                    documentDetailDataRef.current = documentDetailData;
                  }
                  
                  // Mở popup tương ứng với action
                  if (savedAction === 'trinh_giam_doc') {
                    setTrinhGiamDocOpen(true);
                  } else if (savedAction === 'trinh_pho_giam_doc') {
                    setTrinhPhoGiamDocOpen(true);
                  } else if (savedAction === 'chuyen_tham_muu') {
                    setChuyenThamMuuOpen(true);
                  } else if (savedAction === 'chuyen_xu_ly') {
                    setChuyenXuLyOpen(true);
                  }
                } else {
                  // Không có action, đóng dialog và refresh
                  if (!hasUploadError) {
                    enqueueSnackbar(`${mode === 'create' ? 'Tạo' : 'Cập nhật'} văn bản thành công`, { variant: 'success' });
                  } else {
                    enqueueSnackbar(`${mode === 'create' ? 'Tạo' : 'Cập nhật'} văn bản thành công nhưng có lỗi khi upload một số file`, { variant: 'warning' });
                  }
                  onSuccess();
                  handleClose();
                }
                
                setLoading(false);
                setDuplicateCodeData(null);
                pendingUpdateRef.current = null;
              } catch (error: any) {
                console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} document:`, error);
                enqueueSnackbar(error?.response?.data?.msg || error.message || `Có lỗi xảy ra khi ${mode === 'create' ? 'tạo' : 'cập nhật'} văn bản`, { variant: 'error' });
                setLoading(false);
                setDuplicateCodeData(null);
                pendingUpdateRef.current = null;
              }
            }}
            variant="contained"
            color="warning"
            autoFocus
          >
            Tiếp tục {mode === 'create' ? 'tạo mới' : 'cập nhật'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default CreateIncomingDocumentDialog;
